/*!
 * BBIZ AI Shopping Guide — Phase 0 Widget
 * Target: Shopify storefronts (pilot: warrix.my, Kalles v4 / t4s- CSS prefix)
 * Version: 0.4.8
 * Built:   2026-05-31T03:49:29.896Z
 *
 * Active Rules (5):
 *   R1  size_soldout_intercept     — sold-out variant click → notify-me prompt
 *   R2  cart_below_free_shipping   — cart subtotal < threshold → upsell suggestions
 *   R3  mobile_exit_intent         — mobile pagehide w/ items in cart → discount
 *   R4  jersey_size_advisor        — long hesitation on jersey page → size advisor
 *   R5  facebook_low_quality       — facebook referrer + bounce risk → category quick-pick
 *
 * A/B test: 50/50 stable bucketing by sessionId. Both arms emit identical
 * events to window.dataLayer — control just doesn't render UI.
 */

(function () {
  'use strict';

  // Idempotent: don't initialize twice
  if (window.__BBIZ_INITED__) return;
  window.__BBIZ_INITED__ = true;

  // ─── core/config.js ─────────────────────────────────
  // src/core/config.js
  //
  // All tunable knobs in one place. Anything that ops or A/B testing
  // might want to tweak without redeploying lives here. If it's not
  // here, it shouldn't be a knob.

  const CONFIG = {
    siteId: 'warrix-my',
    apiEndpoint: 'https://api.bbiz.example/v1/guide', // placeholder; not called in Phase 0
    currency: 'MYR',
    currencySymbol: 'RM',
    freeShippingThreshold: 120,
    discountThreshold: 100,
    discountCode: 'WELCOME10',
    // Discount is percentage-off (e.g. 10% off ≥ RM100), NOT a fixed amount.
    discountPercent: 10,

    // A/B test
    abTestSplit: 0.5,                   // standard 50/50 A/B test
    // Phase 0.5 collector — widget POSTs each event via sendBeacon. Failures are
    // swallowed in emit(); the widget never blocks on this endpoint.
    analyticsEndpoint: 'https://bbiz-collector.vercel.app/api/events',
    sessionTtlMs: 30 * 60 * 1000,

    // Trigger gate
    // 4s floor — 76% of warrix.my traffic is mobile, where 8s is too long
    // (mobile sessions are shorter and more decision-driven). R2 (12s) / R4
    // (25s) / R5 (10s) override this with their own thresholds.
    minActiveMsBeforeAnyTrigger: 4000,
    triggerCooldownMs: 90 * 1000,       // gap between two triggers in the same session
    maxTriggersPerSession: 2,           // hard cap

    // Logging
    debug: typeof location !== 'undefined' &&
           new URLSearchParams(location.search).has('bbiz_debug'),

    locale: 'en', // warrix.my is mostly English; localize per-merchant in Phase 1

    // ─── Merchant-tunable defaults ────────────────────────────────────────
    // The defaults below match the warrix.my pilot. Override per-merchant
    // via window.__BBIZ_CONFIG__ on the storefront. Every rule reads from
    // CONFIG (no hardcoded merchant copy), so onboarding a new store is
    // mostly about setting these keys in the storefront's Liquid/PHP snippet.

    merchantName: 'WARRIX',

    // Which rules are active. Default: R2+R4+R5+R6+R8 (v0.4.8, 2026-05-31):
    //   - R1 DISABLED (isolation test): live A/B data showed treatment
    //     significantly UNDERPERFORMING control on larrieshoes (ATC 0.71% vs
    //     2.36%, p=0.005) and a milder same-direction dip on warrix, both
    //     coinciding with R1 going live (v0.4.4). Hands-on investigation ruled
    //     out a mechanical bug (ATC works, events fire, no overlay) — the harm
    //     appears behavioral: R1 fires at the *sold-out-size click* moment (a
    //     negative emotional beat) and is dismissed ~64% of the time on both
    //     stores. We pull R1 to isolate it: if treatment recovers to >= control
    //     within 1-2 weeks, R1 is confirmed as the culprit. Re-enable only after
    //     a redesign (later trigger timing + genuinely useful "which sizes ARE
    //     in stock" copy, not a generic browse-more fallback).
    //   - R4: jersey size advisor (warrix only; WC/SiteGiant set isJersey=false).
    //   - R8: passive cart reassurance (redesigned v0.4.7, no checkout intercept).
    //   - R7 STAYS OFF: fabricated urgency. R3 STAYS OFF: needs verified code.
    enabledRules: ['R2', 'R4', 'R5', 'R6', 'R8'],

    // R1 — "see more from the same series" team-affinity mapping.
    // Key is a case-insensitive substring matched against the product handle.
    // Set to {} for stores without team affinity (e.g. a general shoe store).
    r1SeriesMap: {
      nsfc:     { url: '/collections/nsfc-jersey-25-26',       label: 'NSFC jerseys' },
      krw:      { url: '/collections/krw-jersey-25-26',        label: 'KRW jerseys' },
      thailand: { url: '/collections/thailand-jersey-2025-26', label: 'Thailand jerseys' },
    },
    // When no series matches, R1 falls back to a generic "browse more" link.
    // URL is computed at runtime as getAdapter().buildCollectionURL(r1FallbackHandle).
    r1FallbackHandle: 'jersey',
    r1FallbackLabel: 'WARRIX jerseys',

    // R2 — free-shipping nudge add-on
    r2AddonName: 'Shoe Bag',
    r2AddonPrice: 29,
    r2AddonsStartingPrice: 13.30,
    r2AddonURL: '/collections/all?sort_by=price-asc',

    // R5 — Facebook traffic funnel
    r5PrimaryURL: '/collections/nsfc-jersey-25-26',
    r5PrimaryLabel: 'NSFC Jerseys 25/26',
    r5SecondaryURL: '/collections/thailand',
    r5SecondaryLabel: 'Thailand Jerseys',
  };

  // Merge __BBIZ_CONFIG__ overrides — pre-seeded by the storefront's Liquid /
  // PHP snippet. Anything set there wins over the defaults above (shallow:
  // object-valued keys are fully replaced, not merged deep).
  if (typeof window !== 'undefined' && window.__BBIZ_CONFIG__) {
    Object.keys(window.__BBIZ_CONFIG__).forEach(function (k) {
      if (window.__BBIZ_CONFIG__[k] !== undefined) CONFIG[k] = window.__BBIZ_CONFIG__[k];
    });
  }


  // ─── utils/log.js ─────────────────────────────────
  // src/utils/log.js
  //
  // Cheap, conditional console logger. Active only when ?bbiz_debug=1.
  // Always prefixed so it's grep-able in DevTools.

  function log(...args) {
    if (!CONFIG.debug) return;
    console.log('%c[BBIZ]', 'color:#2E86C1;font-weight:bold', ...args);
  }


  // ─── core/adapters/base.js ─────────────────────────────────
  // src/core/adapters/base.js
  //
  // Platform adapter registry. Each adapter encapsulates everything that's
  // specific to a storefront platform (Shopify Kalles, WooCommerce, SiteGiant,
  // ...) so the rule engine and trackers can stay platform-agnostic.
  //
  // Interface every adapter must expose:
  //   name: string                   — set by registerAdapter
  //   detectPageType(): 'product'|'cart'|'collection'|'home'|'checkout'|'other'
  //   extractProduct(): { handle, title, variants[{value,soldout}], availableCount, soldoutCount, isJersey } | null
  //   getCartSubtotal(): number | null
  //   matchVariantSwatch(event): { value, soldout } | null
  //   matchATCButton(event): boolean
  //   triggerNotifyForm(): boolean    — best-effort click of native notify-me UI
  //   selectVariant(value): boolean   — best-effort click of swatch by value
  //   cartPath(): string              — platform's cart URL ('/cart' vs '/cart/')
  //   buildCollectionURL(handle): string

  const PLATFORM_ADAPTERS = {};

  function registerAdapter(name, adapter) {
    adapter.name = name;
    PLATFORM_ADAPTERS[name] = adapter;
  }

  // Best-effort auto-detection. Adapters can register first, then we choose
  // one when getAdapter() is first called. Explicit __BBIZ_CONFIG__.platform
  // always wins over auto-detect.
  function autoDetectPlatform() {
    if (typeof document === 'undefined' || !document.body) return null;
    if (window.__BBIZ_CONFIG__ && window.__BBIZ_CONFIG__.platform) {
      return window.__BBIZ_CONFIG__.platform;
    }
    // WooCommerce sets a body class on every WC-rendered page
    const c = document.body.classList;
    if (c.contains('woocommerce') || c.contains('woocommerce-page') || c.contains('woocommerce-js')) {
      return 'woocommerce';
    }
    // Shopify exposes window.Shopify on every storefront
    if (typeof window.Shopify !== 'undefined' && window.Shopify) {
      return 'shopify_kalles';
    }
    // SiteGiant Webstore: Next.js app with assets on cdn1.sgliteasset.com.
    // Detect by sniffing any script src pointing at that CDN host, OR by the
    // _next/static/chunks path SiteGiant always serves under. Body class is
    // empty on SiteGiant so we can't use class sniffing.
    try {
      const scripts = document.querySelectorAll('script[src]');
      for (let i = 0; i < scripts.length; i++) {
        const src = scripts[i].src || '';
        if (src.indexOf('sgliteasset.com') !== -1 || src.indexOf('/webstore/_next/static/') !== -1) {
          return 'sitegiant';
        }
      }
    } catch (e) { /* CSP or detached document — fall through */ }
    return null;
  }

  let _activeAdapter = null;
  function getAdapter() {
    if (_activeAdapter) return _activeAdapter;
    const detected = autoDetectPlatform();
    // Backwards-compat default: warrix.my predates __BBIZ_CONFIG__.platform,
    // and window.Shopify check catches it. Any other unrecognized platform
    // also falls back to shopify_kalles since that's still the bulk of pilots.
    const name = detected || 'shopify_kalles';
    if (!PLATFORM_ADAPTERS[name]) {
      log('⚠️ Unknown platform "' + name + '", falling back to shopify_kalles');
      _activeAdapter = PLATFORM_ADAPTERS.shopify_kalles;
    } else {
      _activeAdapter = PLATFORM_ADAPTERS[name];
      log('Platform adapter:', name);
    }
    return _activeAdapter;
  }


  // ─── core/adapters/shopify-kalles.js ─────────────────────────────────
  // src/core/adapters/shopify-kalles.js
  //
  // Adapter for Shopify storefronts on Kalles v4 theme (or its sibling T4S;
  // both by The4 Themes, sharing the t4s- CSS class prefix).
  //
  // Pilot: warrix.my. Originally the only DOM target the widget supported.
  // Logic here was moved from utils/extractors.js / utils/page.js / core/trackers.js
  // in the v0.2.0 platform-adapter refactor; behavior is unchanged.

  registerAdapter('shopify_kalles', {
    detectPageType: function () {
      const p = location.pathname;
      if (/\/products\//.test(p)) return 'product';
      if (p === '/cart' || /\/cart\b/.test(p)) return 'cart';
      if (/\/collections\//.test(p)) return 'collection';
      if (/\/checkouts?\//.test(p)) return 'checkout';
      if (p === '/' || p === '') return 'home';
      return 'other';
    },

    extractProduct: function () {
      const variantNodes = document.querySelectorAll('[data-swatch-item][data-value]');
      if (!variantNodes.length) return null;

      const variants = Array.from(variantNodes).map(function (el) {
        return {
          value: el.getAttribute('data-value'),
          soldout: el.classList.contains('is--soldout'),
          element: el,
        };
      });

      const titleEl = document.querySelector('.t4s-product__title, h1.t4s-product__title, h1');
      const title = (titleEl && titleEl.innerText) || document.title;

      const handle = (location.pathname.split('/products/')[1] || '')
        .split('?')[0]
        .split('/')[0];

      return {
        handle: handle,
        title: title.trim(),
        variants: variants,
        availableCount: variants.filter(function (v) { return !v.soldout; }).length,
        soldoutCount: variants.filter(function (v) { return v.soldout; }).length,
        isJersey: /jersey/i.test(title) || /jersey/i.test(handle),
      };
    },

    getCartSubtotal: function () {
      const totalEl = document.querySelector(
        '.t4s-cart__total-price, [class*="cart-total"], [class*="subtotal"]'
      );
      const text = totalEl ? totalEl.innerText : document.body.innerText;
      const match = text.match(/RM\s*([\d,]+\.?\d*)/);
      return match ? parseFloat(match[1].replace(/,/g, '')) : null;
    },

    matchVariantSwatch: function (e) {
      const swatch = e.target.closest && e.target.closest('[data-swatch-item]');
      if (!swatch) return null;
      return {
        value: swatch.getAttribute('data-value'),
        soldout: swatch.classList.contains('is--soldout'),
      };
    },

    matchATCButton: function (e) {
      return !!(e.target.closest && e.target.closest('[name="add"], .t4s-product-form__submit'));
    },

    triggerNotifyForm: function () {
      const btn = document.querySelector('[class*="notify"], [id*="notify"]');
      if (!btn) return false;
      btn.click();
      if (btn.scrollIntoView) btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    },

    selectVariant: function (value) {
      const swatch = document.querySelector('[data-swatch-item][data-value="' + value + '"]');
      if (!swatch) return false;
      swatch.click();
      if (swatch.scrollIntoView) swatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    },

    cartPath: function () { return '/cart'; },
    checkoutPath: function () { return '/checkout'; },

    buildCollectionURL: function (handle) { return '/collections/' + handle; },

    // ─── Cart-page selectors (Layer A1 full-funnel instrumentation) ───────
    // Kalles v4 uses generic Shopify form names plus t4s- themed wrappers.
    // Each cart line has exactly one input[name="updates[]"], so counting
    // those is the most reliable line-count signal.
    cartItemCount: function () {
      return document.querySelectorAll('input[name="updates[]"]').length || null;
    },
    matchCartQtyInput: function (e) {
      return !!(e.target && e.target.matches && e.target.matches(
        'input[name="updates[]"], .t4s-quantity-input, input.t4s-quantity-input'
      ));
    },
    matchCartRemoveButton: function (e) {
      // Kalles cart remove uses the standard Shopify /cart/change?quantity=0
      // link pattern, plus a t4s-themed wrapper class.
      return !!(e.target.closest && e.target.closest(
        'a[href*="quantity=0"], .t4s-page_cart__remove'
      ));
    },
    matchCheckoutButton: function (e) {
      // Covers all checkout entry points on Kalles storefronts:
      //   - <button name="checkout">          ← /cart page submit
      //   - .t4s-btn__checkout                ← Kalles styled cart button (drawer + cart page)
      //   - .shopify-payment-button__button   ← native Shop Pay / Apple Pay / G Pay (PDP + cart)
      //   - [data-shopify="payment-button"]   ← Shopify Payments wrapper (older themes)
      //   - .additional-checkout-buttons      ← Buy with PayPal etc. (some themes)
      return !!(e.target.closest && e.target.closest(
        '[name="checkout"], .t4s-btn__checkout, ' +
        '.shopify-payment-button__button, [data-shopify="payment-button"], ' +
        '.additional-checkout-buttons button'
      ));
    },

    // ─── Cart drawer (Kalles slides in a right-side mini cart after ATC) ───
    //
    // Why this matters: 87% of ATC-clicking users on warrix never visit /cart
    // because the drawer auto-opens and they checkout from there. Without
    // tracking drawer state, the funnel showed "cart_view drops 87% after ATC"
    // which was misleading — it was a measurement gap, not real abandonment.
    cartDrawerElement: function () {
      try {
        if (typeof document === 'undefined' || !document.getElementById) return null;
        return document.getElementById('t4s-mini_cart');
      } catch (e) { return null; }
    },
    isCartDrawerVisible: function (el) {
      if (!el || !el.classList) return false;
      // Kalles toggles `t4s-dn` (display:none) when closing the drawer.
      // The drawer is also reachable via mobile menu toggle, but only the
      // right-side mini cart instance has id='t4s-mini_cart'.
      return !el.classList.contains('t4s-dn');
    },
  });


  // ─── core/adapters/woocommerce.js ─────────────────────────────────
  // src/core/adapters/woocommerce.js
  //
  // Adapter for WooCommerce storefronts. Tested against larrieshoes.com
  // (hello-elementor-child theme + Woo Variation Swatches plugin).
  //
  // Key differences from Shopify Kalles:
  //   - Page type comes from <body> class (set by WC server-side, no URL parsing)
  //   - Variant data is a complete JSON blob on form.variations_form
  //     (data-product_variations), so we don't need to scrape DOM
  //   - h1 is the only reliable title selector (Elementor sites have multiple
  //     .product_title matches from related-products widgets)
  //   - Variant swatches are rendered by WVS plugin as .variable-item

  registerAdapter('woocommerce', {
    detectPageType: function () {
      const c = document.body.classList;
      if (c.contains('single-product')) return 'product';
      if (c.contains('woocommerce-cart')) return 'cart';
      // order-received MUST be checked before checkout: WC keeps the
      // 'woocommerce-checkout' body class on the thank-you page since it's
      // technically under /checkout/order-received/. The more specific class
      // wins so we classify it as the terminal funnel stage, not yet-checking-out.
      if (c.contains('woocommerce-order-received')) return 'order_complete';
      if (c.contains('woocommerce-checkout')) return 'checkout';
      if (c.contains('woocommerce-shop') ||
          c.contains('tax-product_cat') ||
          c.contains('tax-product_tag') ||
          (c.contains('archive') && c.contains('woocommerce'))) {
        return 'collection';
      }
      const p = location.pathname;
      if (p === '/' || p === '') return 'home';
      return 'other';
    },

    extractProduct: function () {
      const form = document.querySelector('form.variations_form');
      if (!form) return null;

      let raw;
      try { raw = JSON.parse(form.getAttribute('data-product_variations') || '[]'); }
      catch (e) { return null; }
      if (!Array.isArray(raw) || !raw.length) return null;

      // Collapse multi-attribute variants down to size-level. If multiple variants
      // share the same size (different colors), in-stock wins over sold-out.
      const SIZE_KEYS = ['attribute_pa_size', 'attribute_size'];
      const map = {};
      raw.forEach(function (v) {
        const attrs = v.attributes || {};
        let sizeKey = SIZE_KEYS.find(function (k) { return attrs[k]; });
        if (!sizeKey) sizeKey = Object.keys(attrs)[0]; // fallback: any first attribute
        if (!sizeKey) return;
        const value = attrs[sizeKey];
        if (!value) return;
        if (map[value] && !map[value].soldout) return; // keep in-stock entry
        map[value] = {
          value: value,
          soldout: !v.is_in_stock,
          price: v.display_price,
          sku: v.sku,
        };
      });

      const variants = Object.keys(map).map(function (k) { return map[k]; });
      if (!variants.length) return null;

      // h1 is page-unique on WC single-product. .product_title matches multiple
      // related-product widgets on Elementor sites.
      const titleEl = document.querySelector('h1') || document.querySelector('.product_title');
      const title = (titleEl && titleEl.innerText) || document.title;

      // Handle = URL slug (last non-empty segment)
      const segments = location.pathname.split('/').filter(function (s) { return s; });
      const handle = segments[segments.length - 1] || '';

      return {
        handle: handle,
        title: title.trim(),
        variants: variants,
        availableCount: variants.filter(function (v) { return !v.soldout; }).length,
        soldoutCount: variants.filter(function (v) { return v.soldout; }).length,
        isJersey: /jersey/i.test(title) || /jersey/i.test(handle),
      };
    },

    getCartSubtotal: function () {
      const el = document.querySelector(
        '.cart-subtotal .woocommerce-Price-amount, ' +
        '.cart-subtotal .amount, ' +
        '.cart_totals .order-total .woocommerce-Price-amount'
      );
      if (!el) return null;
      const text = el.innerText || el.textContent || '';
      const match = text.match(/([\d,]+\.\d{2})/) || text.match(/([\d,]+)/);
      return match ? parseFloat(match[1].replace(/,/g, '')) : null;
    },

    matchVariantSwatch: function (e) {
      // WVS plugin renders swatches as .variable-item (LI / DIV depending on type)
      const swatch = e.target.closest && e.target.closest('.variable-item');
      if (!swatch) return null;
      return {
        value: swatch.getAttribute('data-value'),
        soldout: swatch.classList.contains('disabled') ||
                 swatch.classList.contains('out-of-stock') ||
                 swatch.classList.contains('wvs-disabled'),
      };
    },

    matchATCButton: function (e) {
      return !!(e.target.closest && e.target.closest('.single_add_to_cart_button'));
    },

    triggerNotifyForm: function () {
      // WC doesn't ship a native notify-me UI. Common plugins (Back In Stock,
      // WPC Waitlist, etc.) each use their own selector — best-effort fuzzy match.
      const btn = document.querySelector(
        '[class*="notify"], [id*="notify"], [class*="waitlist"], [class*="back-in-stock"]'
      );
      if (!btn) return false;
      btn.click();
      if (btn.scrollIntoView) btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    },

    selectVariant: function (value) {
      const swatch = document.querySelector('.variable-item[data-value="' + value + '"]');
      if (!swatch) return false;
      swatch.click();
      if (swatch.scrollIntoView) swatch.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    },

    cartPath: function () { return '/cart/'; },
    checkoutPath: function () { return '/checkout/'; },

    buildCollectionURL: function (handle) {
      // WC default product archive is /product-category/{slug}/.
      // 'jersey'/'shop'/'all' are conventional aliases for "everything" — map to /shop/.
      if (!handle || handle === 'all' || handle === 'shop' || handle === 'jersey') {
        return '/shop/';
      }
      return '/product-category/' + handle + '/';
    },

    // ─── Cart-page selectors (Layer A1 full-funnel instrumentation) ───────
    // larrieshoes uses default WC cart markup + Elementor cart widget for the
    // "remove" icon. Each line has one input.qty so we count those for items.
    cartItemCount: function () {
      return document.querySelectorAll('tr.cart_item input.qty').length || null;
    },
    matchCartQtyInput: function (e) {
      return !!(e.target && e.target.matches && e.target.matches(
        'input.qty, input[name^="cart["][name$="[qty]"]'
      ));
    },
    matchCartRemoveButton: function (e) {
      // Standard WC: .product-remove a.remove. Elementor's mini-cart widget
      // adds .elementor_remove_from_cart_button. Some themes use a.remove_from_cart_button.
      return !!(e.target.closest && e.target.closest(
        '.product-remove a.remove, a.remove_from_cart_button, .elementor_remove_from_cart_button'
      ));
    },
    matchCheckoutButton: function (e) {
      return !!(e.target.closest && e.target.closest(
        'a.checkout-button, .checkout-button.wc-forward, .wc-proceed-to-checkout a.checkout-button'
      ));
    },

    // WooCommerce default themes use a full /cart page, no cart drawer.
    // Some Elementor / WC theme builders add a mini-cart widget but it's
    // not standardized. Adapter returns null until a specific WC theme
    // pilot needs drawer tracking.
    cartDrawerElement: function () { return null; },
    isCartDrawerVisible: function () { return false; },

    // ─── Checkout-page selectors (Layer A2 full-funnel instrumentation) ───
    matchPlaceOrderButton: function (e) {
      return !!(e.target.closest && e.target.closest(
        '#place_order, button#place_order, button[name="woocommerce_checkout_place_order"]'
      ));
    },

    // ─── Order-complete data extraction ───────────────────────────────────
    // The thank-you page exposes order id + total in well-known WC markup.
    // We read both so the dashboard can compute true paid AOV per arm.
    extractOrderInfo: function () {
      const idEl = document.querySelector(
        '.woocommerce-order-overview__order strong, .order strong, ' +
        'li.woocommerce-order-overview__order strong'
      );
      const totalEl = document.querySelector(
        '.woocommerce-order-overview__total strong .woocommerce-Price-amount, ' +
        '.woocommerce-order-overview__total .woocommerce-Price-amount, ' +
        '.order_details .order-total .woocommerce-Price-amount'
      );
      const idText = idEl ? (idEl.innerText || idEl.textContent || '').trim() : null;
      const totalText = totalEl ? (totalEl.innerText || totalEl.textContent || '') : '';
      const totalMatch = totalText.match(/([\d,]+\.\d{2})/) || totalText.match(/([\d,]+)/);
      return {
        order_id: idText || null,
        order_total: totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : null,
      };
    },
  });


  // ─── core/adapters/sitegiant.js ─────────────────────────────────
  // src/core/adapters/sitegiant.js
  //
  // Adapter for SiteGiant Webstore storefronts. Pilot target: victorsports.my
  // (USS Group's badminton/sports brand — flagship SiteGiant tenant).
  //
  // Platform characteristics (gathered via live DOM inspection, 2026-05-17):
  //   - Engine: Next.js 13+ App Router (React SPA, no __NEXT_DATA__ exposed)
  //   - UI library: Ant Design (class prefix `ant-`)
  //   - Static asset host: cdn1.sgliteasset.com (`_next/static/chunks/`)
  //   - CSS strategy: styled-components → class names like `sc-XXXX-N HASH`
  //     followed by stable semantic suffix (`product-variants`, `variant-div`).
  //     We MUST select on the semantic class, not the `sc-` prefix.
  //   - Navigation: full SPA — route changes don't reload the page. The widget's
  //     PAGE.type tracking and per-route events rely on src/core/spa.js patching
  //     history.pushState (see that file for the SPA handler).
  //
  // URL patterns (note the singular/plural twist):
  //   /                              home
  //   /product/{slug}                product detail (singular!)
  //   /products/{collection-slug}    collection (plural!)
  //   /cart                          cart
  //   /checkout                      checkout
  //   /order(-confirmation|-success) thank-you (selector TBD — needs live order)

  registerAdapter('sitegiant', {
    detectPageType: function () {
      // SiteGiant's body has no useful classes (`body class=""` is empty on
      // every page), so we go by URL. Order matters — most-specific first.
      const p = location.pathname;
      if (p === '/' || p === '') return 'home';
      if (p === '/cart' || p.indexOf('/cart') === 0) return 'cart';
      if (p.indexOf('/checkout') === 0) return 'checkout';
      if (/^\/order(?:-confirmation|-success|s\/)/.test(p)) return 'order_complete';
      // Singular /product/{slug} is PDP; plural /products/{slug} is collection.
      if (p.indexOf('/product/') === 0) return 'product';
      if (p.indexOf('/products/') === 0 || p.indexOf('/products') === 0) return 'collection';
      return 'other';
    },

    extractProduct: function () {
      const variantNodes = document.querySelectorAll('.variant-div');
      if (!variantNodes.length) return null;

      // Each .variant-div renders a selectable variant button. Sold-out detection
      // is a heuristic — combine class-name and text-content checks because
      // SiteGiant doesn't expose a clean ARIA/data-attribute for stock state.
      const variants = Array.from(variantNodes).map(function (v) {
        const txt = (v.textContent || '').trim();
        const cls = v.className || '';
        const soldout =
          /sold[\s-]?out|out of stock/i.test(txt) ||
          /sold-out|disabled|unavailable|oos/i.test(cls);
        return { value: txt, soldout: soldout };
      });

      // Title: h1.h1-title is the canonical PDP heading. .product-name appears
      // in multiple places (related products, add-ons), so prefer h1.
      const titleEl = document.querySelector('h1.h1-title') ||
                      document.querySelector('.product-name');
      const title = (titleEl && (titleEl.innerText || titleEl.textContent)) || document.title;

      const handleMatch = location.pathname.match(/\/product\/([^/?#]+)/);
      const handle = handleMatch ? handleMatch[1] : '';

      return {
        handle: handle,
        title: (title || '').trim(),
        variants: variants,
        availableCount: variants.filter(function (v) { return !v.soldout; }).length,
        soldoutCount: variants.filter(function (v) { return v.soldout; }).length,
        // SiteGiant pilot is sports gear (rackets/shoes/bags) — no jersey logic.
        // The `isJersey` flag exists for warrix-specific R4. SiteGiant adapters
        // can keep this false and R4 simply won't fire (which is correct, since
        // R4 is gated on `isJersey === true`).
        isJersey: false,
      };
    },

    getCartSubtotal: function () {
      // Cart subtotal lives in `.sub-total` (display-medium bold). Falls back
      // to `.cart-subtotal` row text if the specific class isn't found.
      const el = document.querySelector(
        '.sub-total, .cart-totals .sub-total, .cart-subtotal .sub-total'
      );
      const text = (el && (el.innerText || el.textContent)) || '';
      const match = text.match(/RM\s*([\d,]+\.?\d*)/i) ||
                    text.match(/([\d,]+\.\d{2})/);
      return match ? parseFloat(match[1].replace(/,/g, '')) : null;
    },

    matchVariantSwatch: function (e) {
      // .variant-div is the button wrapper; .variant-text is the inner span.
      const swatch = e.target.closest && e.target.closest('.variant-div, .variant-text');
      if (!swatch) return null;
      return {
        value: (swatch.textContent || '').trim(),
        // Class-based check only here — text-based check would misfire on the
        // "Sold Out" label inside the swatch.
        soldout: /sold-out|disabled|unavailable/i.test(swatch.className || ''),
      };
    },

    matchATCButton: function (e) {
      // SiteGiant renders ATC as `<button class="ant-btn ant-btn-text primary-btn">`.
      // Several primary-btn buttons exist per page (BUY NOW, ADD TO CART, related-
      // product CTAs), so we MUST filter by text content. Capture-phase click
      // listener in trackers.js calls this — keep it cheap.
      const btn = e.target.closest && e.target.closest('button.ant-btn, button.primary-btn');
      if (!btn) return false;
      const txt = (btn.textContent || '').trim();
      return /add\s*to\s*cart/i.test(txt);
    },

    triggerNotifyForm: function () {
      // SiteGiant doesn't ship a native notify-me / waitlist widget. Fuzzy
      // match in case the merchant has installed a 3rd-party one.
      const btn = document.querySelector(
        '[class*="notify"], [id*="notify"], [class*="waitlist"]'
      );
      if (!btn) return false;
      btn.click();
      if (btn.scrollIntoView) btn.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    },

    selectVariant: function (value) {
      // Click the .variant-div whose text exactly matches. Used by R1 to flip
      // a sold-out swatch to the suggested alternative.
      const all = Array.from(document.querySelectorAll('.variant-div'));
      const match = all.find(function (v) {
        return (v.textContent || '').trim() === value;
      });
      if (!match) return false;
      match.click();
      if (match.scrollIntoView) match.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return true;
    },

    cartPath: function () { return '/cart'; },
    checkoutPath: function () { return '/checkout'; },

    buildCollectionURL: function (handle) {
      // Plural /products/{slug} — opposite convention from /product/{slug}.
      // Falls back to /products/all-products for "everything" handles.
      if (!handle || handle === 'all' || handle === 'shop') {
        return '/products/all-products';
      }
      return '/products/' + handle;
    },

    // ─── Cart-page selectors (Layer A1 full-funnel instrumentation) ────────
    cartItemCount: function () {
      // Cart is an Ant Design table — rows live under .ant-table-tbody.
      // When empty, the tbody is replaced with .ant-table-empty placeholder.
      return document.querySelectorAll('.ant-table-tbody tr').length || null;
    },
    matchCartQtyInput: function (e) {
      // Ant InputNumber wraps a disabled input plus +/- buttons. The actual
      // numeric input has class `.ant-input-number-input`. We listen on
      // change events (commit), not input (per-keystroke).
      return !!(e.target && e.target.matches && e.target.matches(
        '.ant-input-number-input, .input-quantity-counter input'
      ));
    },
    matchCartRemoveButton: function (e) {
      // Remove icons typically use Ant Icons with aria-label="delete" / "close",
      // wrapped in clickable spans. We match broadly since the exact class
      // is hard to pin down without an items-in-cart inspection.
      return !!(e.target.closest && e.target.closest(
        '[class*="remove-item"], [class*="delete-cart"], [class*="cart-delete"], ' +
        'button[aria-label*="remove" i], .ant-table-tbody [class*="anticon-delete"], ' +
        '.ant-table-tbody [class*="anticon-close"]'
      ));
    },
    matchCheckoutButton: function (e) {
      // Cart-page "Proceed to Checkout" button. Empty-cart inspection didn't
      // surface a stable class, so we match generously: any button/link
      // containing "checkout" / "proceed" text, gated to the cart page later
      // by the rule's PAGE.type check.
      const btn = e.target.closest && e.target.closest('button, a');
      if (!btn) return false;
      if (btn.matches('[class*="checkout-btn"], [class*="proceed-checkout"]')) return true;
      const txt = (btn.textContent || '').trim();
      return /proceed\s*to\s*checkout|^check\s*out$/i.test(txt) && btn.closest('.cart-totals, [class*="cart-summary"], [class*="cart-footer"]');
    },

    // SiteGiant Webstore — no widely-observed cart drawer pattern in pilot
    // (victorsports.my uses /cart page navigation). Return null until we
    // confirm a SiteGiant tenant has a drawer worth tracking.
    cartDrawerElement: function () { return null; },
    isCartDrawerVisible: function () { return false; },

    // ─── Checkout-page selectors (Layer A2) ────────────────────────────────
    matchPlaceOrderButton: function (e) {
      // Checkout final commit button. SiteGiant likely uses
      // `<button class="ant-btn ant-btn-primary">Place Order</button>` — text-
      // matched defensively until we can inspect a real checkout flow.
      const btn = e.target.closest && e.target.closest('button.ant-btn-primary, button[type="submit"]');
      if (!btn) return false;
      const txt = (btn.textContent || '').trim();
      return /place\s*order|confirm\s*order|complete\s*order|pay\s*now/i.test(txt);
    },

    extractOrderInfo: function () {
      // Order-success page — selectors are best-effort until we can inspect
      // a real order-received page. Defensive class-name + text search.
      const idEl = document.querySelector(
        '[class*="order-id"], [class*="order-number"], [class*="invoice-no"]'
      );
      const totalEl = document.querySelector(
        '[class*="order-total"], [class*="grand-total"], .sub-total'
      );
      const idText = idEl ? (idEl.innerText || idEl.textContent || '').trim() : null;
      const totalText = totalEl ? (totalEl.innerText || totalEl.textContent || '') : '';
      const totalMatch = totalText.match(/RM\s*([\d,]+\.?\d*)/i) ||
                         totalText.match(/([\d,]+\.\d{2})/);
      return {
        order_id: idText || null,
        order_total: totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : null,
      };
    },
  });


  // ─── utils/page.js ─────────────────────────────────
  // src/utils/page.js
  //
  // Where am I? Used by the rule engine to gate page-specific rules.
  //
  // Page-type detection is delegated to the active platform adapter (see
  // src/core/adapters/). Referrer kind is platform-independent — we read it
  // from document.referrer and bucket it for R5.
  //
  // SPA route changes update PAGE.type from src/core/spa.js (Shopify only;
  // WooCommerce storefronts are server-rendered, no SPA navigation).

  function detectReferrerKind() {
    const r = (document.referrer || '').toLowerCase();
    if (!r) return 'direct';
    if (/facebook|fb\.com|fb\.me/.test(r)) return 'facebook';
    if (/instagram/.test(r)) return 'instagram';
    if (/google/.test(r)) return 'google';
    if (/tiktok/.test(r)) return 'tiktok';
    try {
      if (location.hostname === new URL(r).hostname) return 'internal';
    } catch (e) { /* malformed referrer */ }
    return 'other';
  }

  const PAGE = {
    type: getAdapter().detectPageType(),
    referrer: detectReferrerKind(),
  };


  // ─── core/session.js ─────────────────────────────────
  // src/core/session.js
  //
  // Stable per-session identity and A/B bucketing.
  //
  // The session id is generated once and pinned in sessionStorage with a
  // 30-min sliding expiry. Bucket assignment is a deterministic hash of
  // `sessionId + ':ab'` so the same visitor always lands in the same arm.
  //
  // We deliberately do NOT use localStorage or cookies here — sessionStorage // lint-disable: no-localStorage
  // gives us the right scope (this tab, this visit) and is GDPR-trivial.

  function getSessionId() {
    const KEY = 'bbiz_sid';
    try {
      const raw = sessionStorage.getItem(KEY);
      if (raw) {
        const obj = JSON.parse(raw);
        if (obj.exp > Date.now()) return obj.id;
      }
    } catch (e) { /* sessionStorage may be blocked; fall through */ }

    const id = 's_' + Math.random().toString(36).slice(2) + '_' + Date.now().toString(36);
    try {
      sessionStorage.setItem(KEY, JSON.stringify({
        id,
        exp: Date.now() + CONFIG.sessionTtlMs,
      }));
    } catch (e) { /* best-effort */ }
    return id;
  }

  // FNV-1a 32-bit hash → uniform [0, 1)
  function hashToUnit(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return ((h >>> 0) % 10000) / 10000;
  }

  const SESSION_ID = getSessionId();

  // Debug-only override: ?bbiz_debug=1&bbiz_force_arm=treatment (or control)
  // Lets engineers force a bucket for testing without reshuffling sessionStorage.
  const AB_BUCKET = (function () {
    if (CONFIG.debug) {
      try {
        const p = new URLSearchParams(location.search).get('bbiz_force_arm');
        if (p === 'treatment' || p === 'control') {
          log('AB bucket FORCED via ?bbiz_force_arm=' + p);
          return p;
        }
      } catch (e) { /* malformed query string */ }
    }
    return hashToUnit(SESSION_ID + ':ab') < CONFIG.abTestSplit ? 'treatment' : 'control';
  })();

  log('Session', SESSION_ID, 'AB bucket =', AB_BUCKET);

  // Surface AB_BUCKET to the Shopify Customer Events Pixel sandbox via a
  // first-party cookie. The Pixel runs in an iframe with a different origin
  // and cannot read sessionStorage; cookies on the storefront domain are
  // readable via the Pixel's browser.cookie API. Cookie holds only the
  // bucket label ("treatment" / "control") — no PII, no GDPR concern.
  try {
    const _bbizMaxAge = Math.floor(CONFIG.sessionTtlMs / 1000);
    document.cookie = 'bbiz_ab=' + AB_BUCKET + '; path=/; max-age=' + _bbizMaxAge + '; SameSite=Lax'; // lint-disable: no-cookies
  } catch (e) { /* SameSite or storage restrictions — Pixel falls back to ab_bucket=unknown */ }


  // ─── core/analytics.js ─────────────────────────────────
  // src/core/analytics.js
  //
  // Single egress point for all analytics. Pushes to window.dataLayer with
  // a `bbiz_*` event name and a normalized payload envelope. Both A/B arms
  // emit identical events — that's how we measure lift.
  //
  // Downstream wiring (GA4, Shopify Customer Events, server-side BigQuery)
  // happens in the merchant's tag manager / pixel config, not here.

  window.dataLayer = window.dataLayer || [];

  function emit(event, payload) {
    payload = payload || {};
    const evtName = 'bbiz_' + event;
    const evtData = {
      site_id: CONFIG.siteId,
      session_id: SESSION_ID,
      ab_bucket: AB_BUCKET,
      page_type: PAGE.type,
      page_url: location.pathname,
      ts: Date.now(),
      ...payload,
    };

    // (1) GTM-compatible dataLayer push — works for any merchant with GTM/GA4 wired up.
    window.dataLayer.push({ event: evtName, bbiz: evtData });

    // (2) Shopify Customer Events Pixel — readable by sandboxed Custom Pixels installed
    // via Shopify admin → Settings → Customer events. Wrapped in try/catch so a missing
    // Pixel API never blocks the widget.
    try {
      if (window.Shopify && window.Shopify.analytics &&
          typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(evtName, evtData);
      }
    } catch (e) { /* never block on analytics */ }

    // (3) Optional first-party collector — only if CONFIG.analyticsEndpoint is set.
    // Uses sendBeacon so events survive page navigation (SPA route changes, exit).
    if (CONFIG.analyticsEndpoint) {
      try {
        const body = JSON.stringify({ name: evtName, data: evtData });
        if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
          const blob = new Blob([body], { type: 'application/json' });
          navigator.sendBeacon(CONFIG.analyticsEndpoint, blob);
        } else if (typeof fetch === 'function') {
          fetch(CONFIG.analyticsEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: body,
            keepalive: true,
          }).catch(function () { /* never block on analytics */ });
        }
      } catch (e) { /* never block on analytics */ }
    }

    log('emit →', event, payload);
  }


  // ─── utils/extractors.js ─────────────────────────────────
  // src/utils/extractors.js
  //
  // Thin wrappers over the active platform adapter. Kept as a separate module
  // so the rest of the widget can keep using getProductData() / getCartSubtotal()
  // without knowing which platform is active.
  //
  // To support a new platform, do NOT fork this file — add a new adapter under
  // src/core/adapters/ and register it in adapters/base.js's auto-detect.

  function getProductData() {
    if (PAGE.type !== 'product') return null;
    return getAdapter().extractProduct();
  }

  function getCartSubtotal() {
    return getAdapter().getCartSubtotal();
  }


  // ─── core/state.js ─────────────────────────────────
  // src/core/state.js
  //
  // Behavioral state machine + per-session counters.
  //
  // States progress monotonically based on engagement signals:
  //
  //   BROWSING ─────► INTERESTED ─────► HESITATING ─────► DECIDING
  //   (default)      (>10s active)      (>25s or         (>60s or scroll>70%
  //                  or scroll>20%)     scroll>40% or    or any add-to-cart)
  //                                     soldout-click)
  //
  // Rules generally fire in HESITATING or DECIDING. Earlier states are too
  // noisy — we'd interrupt people who are just landing.

  const STATE = {
    name: 'BROWSING',
    pageEnteredAt: Date.now(),
    activeMs: 0,                    // engaged time (excludes hidden tab)
    activeStart: Date.now(),
    triggered: new Set(),           // rule ids that already fired this session
    lastTriggerAt: 0,
    triggerCount: 0,
    addToCartClicks: 0,
    soldoutClickCount: 0,
    lastClickedVariant: null,
    lastClickedVariantSoldout: false,
    scrollMaxPct: 0,
    product: null,
    isMobile: window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent),

    // R6 cart-exit-intent state (Layer B).
    // exitIntentDetected: set true by trackers.js when desktop cursor leaves
    //   toward the top of the viewport while on /cart. Sticky once set.
    // checkoutClicked: set true by cart-trackers.js when user clicks the cart
    //   page checkout button. Used by R6 to suppress firing after commit.
    exitIntentDetected: false,
    checkoutClicked: false,
  };

  function transitionTo(name) {
    if (STATE.name === name) return;
    log('state', STATE.name, '→', name);
    emit('state_change', { from: STATE.name, to: name, active_ms: STATE.activeMs });
    STATE.name = name;
  }

  function evaluateStateMachine() {
    if (STATE.activeMs > 60000 || STATE.scrollMaxPct > 70 || STATE.addToCartClicks > 0) {
      transitionTo('DECIDING');
    } else if (STATE.activeMs > 25000 || STATE.scrollMaxPct > 40 || STATE.soldoutClickCount > 0) {
      transitionTo('HESITATING');
    } else if (STATE.activeMs > 10000 || STATE.scrollMaxPct > 20) {
      transitionTo('INTERESTED');
    }
  }


  // ─── core/trigger-gate.js ─────────────────────────────────
  // src/core/trigger-gate.js
  //
  // Centralized rate-limit and arm-suppression for rules.
  //
  // Rules call `canTrigger(ruleId)` to ask permission. If granted they call
  // `fireTrigger(ruleId, payload)`. Control-arm sessions get the same
  // `rule_triggered` event but no UI — that's how the A/B compare stays clean.

  function canTrigger(ruleId) {
    if (STATE.triggered.has(ruleId)) return false;
    if (STATE.activeMs < CONFIG.minActiveMsBeforeAnyTrigger) return false;
    if (STATE.triggerCount >= CONFIG.maxTriggersPerSession) return false;
    if (Date.now() - STATE.lastTriggerAt < CONFIG.triggerCooldownMs) return false;
    return true;
  }

  function fireTrigger(ruleId, payload) {
    STATE.triggered.add(ruleId);
    STATE.lastTriggerAt = Date.now();
    STATE.triggerCount += 1;
    emit('rule_triggered', Object.assign({ rule: ruleId }, payload));

    if (AB_BUCKET === 'control') {
      log('control arm — suppressing UI for', ruleId);
      return;
    }
    // Inject _ruleId into the payload so showDialog / closeDialog / handleCta
    // can tag dialog_shown / dialog_closed / cta_click events with the same
    // ruleId that rule_triggered used. Before this fix (v0.4.3, 2026-05-18),
    // dialog_shown was emitted with rule=<dialog title> which fragmented the
    // per-rule funnel: triggered events and shown/click events didn't aggregate.
    showDialog(Object.assign({ _ruleId: ruleId }, payload));
  }


  // ─── ui/dialog.js ─────────────────────────────────
  // src/ui/dialog.js
  //
  // Shadow-DOM dialog. All UI is mounted on `<div id="bbiz-host">` attached
  // to documentElement, with a closed Shadow Root so the merchant's CSS
  // can't reach in and ours can't leak out.
  //
  // We deliberately use string-template HTML rather than DOM APIs — it's
  // shorter and more readable. There is no untrusted HTML interpolation
  // here; all dynamic strings flow through .textContent.

  const DIALOG_HTML = (
    '<style>' +
      ':host, * { box-sizing: border-box; }' +
      '.panel {' +
        'position: fixed; right: 16px; width: 340px; max-width: calc(100vw - 32px);' +
        // env(safe-area-inset-bottom) keeps the panel above iPhone home indicator
        'bottom: max(16px, env(safe-area-inset-bottom));' +
        'background: #fff; color: #1a1a1a;' +
        "font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;" +
        'border-radius: 12px; box-shadow: 0 10px 40px rgba(0,0,0,0.18);' +
        'padding: 18px 20px 16px; border: 1px solid #e5e7eb;' +
        'transform: translateY(20px); opacity: 0; pointer-events: none;' +
        'transition: all 0.28s ease-out;' +
      '}' +
      '.panel.open { transform: translateY(0); opacity: 1; pointer-events: auto; }' +
      '@media (max-width: 480px) {' +
        '.panel {' +
          'right: 12px; left: 12px; width: auto;' +
          // warrix.my Kalles theme has a sticky bottom nav on mobile (~85px tall:
          // Shop / Wishlist / Cart / Account / Search). Reserve room above it +
          // env() for iPhone home indicator. If we shipped without this, the
          // dialog's CTA buttons would be hidden behind the nav bar.
          'bottom: calc(96px + env(safe-area-inset-bottom));' +
        '}' +
      '}' +
      '.head { display:flex; align-items:center; gap:10px; margin-bottom:10px; }' +
      '.avatar {' +
        'width: 32px; height: 32px; border-radius: 50%;' +
        'background: linear-gradient(135deg,#2E86C1,#1A3C5E); color:#fff;' +
        'display:flex; align-items:center; justify-content:center;' +
        'font-weight:600; font-size:13px; flex-shrink:0;' +
      '}' +
      '.name { font-size: 14px; font-weight: 600; color: #111827; }' +
      '.sub { font-size: 11px; color: #6b7280; }' +
      '.close {' +
        'margin-left:auto; background:none; border:0; cursor:pointer;' +
        'color:#9ca3af; font-size:18px; line-height:1;' +
        // Larger tap target for touch devices (~38x38 effective hit area)
        'padding:10px; border-radius:50%; min-width:38px; min-height:38px;' +
        'display:flex; align-items:center; justify-content:center;' +
        'transition: background 0.15s, color 0.15s;' +
      '}' +
      '.close:hover, .close:active { color:#374151; background:#f3f4f6; }' +
      '.title { font-size: 15px; font-weight: 600; color: #111827; margin: 4px 0 6px; }' +
      '.msg { font-size: 13.5px; line-height: 1.5; color: #374151; margin-bottom: 14px; }' +
      '.actions { display:flex; gap:8px; flex-direction: column; }' +
      '.btn {' +
        'width:100%; border:0; padding:10px 14px; font-size:13.5px; font-weight:600;' +
        'border-radius: 8px; cursor:pointer; transition: all 0.15s; font-family:inherit;' +
      '}' +
      '.btn-primary { background:#1A3C5E; color:#fff; }' +
      '.btn-primary:hover { background:#2E86C1; }' +
      '.btn-secondary { background:#f3f4f6; color:#374151; }' +
      '.btn-secondary:hover { background:#e5e7eb; }' +
      '.badge {' +
        'display:inline-block; background:#fef3c7; color:#92400e;' +
        'font-size:10px; font-weight:600; padding:2px 8px; border-radius:10px;' +
        'margin-left:6px; vertical-align:middle;' +
      '}' +
    '</style>' +
    '<div class="panel" role="dialog" aria-live="polite">' +
      '<div class="head">' +
        '<div class="avatar">W</div>' +
        '<div>' +
          '<div class="name">WARRIX Assistant<span class="badge">BETA</span></div>' +
          '<div class="sub">Here to help you find the right gear</div>' +
        '</div>' +
        '<button class="close" data-action="close" aria-label="Close">×</button>' +
      '</div>' +
      '<div class="title" data-slot="title"></div>' +
      '<div class="msg" data-slot="msg"></div>' +
      '<div class="actions">' +
        '<button class="btn btn-primary" data-slot="cta-primary"></button>' +
        '<button class="btn btn-secondary" data-slot="cta-secondary"></button>' +
      '</div>' +
    '</div>'
  );

  let shadowRoot = null;
  let activePayload = null;

  function ensureShadow() {
    if (shadowRoot) return shadowRoot;
    const host = document.createElement('div');
    host.id = 'bbiz-host';
    host.style.cssText = 'all:initial;position:fixed;z-index:2147483647;';
    document.documentElement.appendChild(host);
    shadowRoot = host.attachShadow({ mode: 'closed' });
    shadowRoot.innerHTML = DIALOG_HTML;
    bindDialogEvents();
    return shadowRoot;
  }

  function bindDialogEvents() {
    shadowRoot.addEventListener('click', function (ev) {
      const slot = ev.target.getAttribute && ev.target.getAttribute('data-slot');
      const action = ev.target.getAttribute && ev.target.getAttribute('data-action');
      if (action === 'close') return closeDialog('close');
      if (slot === 'cta-primary' && activePayload) return handleCta(activePayload.ctaPrimary, 'primary');
      if (slot === 'cta-secondary' && activePayload) return handleCta(activePayload.ctaSecondary, 'secondary');
    });
  }

  function showDialog(payload) {
    const root = ensureShadow();
    const panel = root.querySelector('.panel');
    root.querySelector('[data-slot="title"]').textContent = payload.title || '';
    root.querySelector('[data-slot="msg"]').textContent = payload.message || '';

    const p = root.querySelector('[data-slot="cta-primary"]');
    const s = root.querySelector('[data-slot="cta-secondary"]');
    if (payload.ctaPrimary) {
      p.textContent = payload.ctaPrimary.label;
      p.style.display = '';
    } else {
      p.style.display = 'none';
    }
    if (payload.ctaSecondary) {
      s.textContent = payload.ctaSecondary.label;
      s.style.display = '';
    } else {
      s.style.display = 'none';
    }

    activePayload = payload;
    requestAnimationFrame(function () { panel.classList.add('open'); });
    // Tag with the rule ID (e.g. "R2_cart_below_free_shipping") so the
    // dashboard's per-rule funnel query can aggregate rule_triggered +
    // dialog_shown + cta_click + dialog_closed under one identifier.
    // Falls back to payload.title only if _ruleId is somehow absent (shouldn't
    // happen now that fireTrigger always injects it).
    emit('dialog_shown', { rule: payload._ruleId || payload.title });
  }

  function closeDialog(reason) {
    if (!shadowRoot) return;
    const panel = shadowRoot.querySelector('.panel');
    panel.classList.remove('open');
    emit('dialog_closed', {
      rule: (activePayload && activePayload._ruleId) || null,
      reason: reason,
    });
  }

  function handleCta(cta, kind) {
    if (!cta) return;
    emit('cta_click', {
      rule: (activePayload && activePayload._ruleId) || null,
      kind: kind,
      action: cta.action,
      value: cta.value,
    });

    switch (cta.action) {
      case 'navigate':
        if (cta.value) location.href = cta.value;
        break;

      case 'select_variant': {
        getAdapter().selectVariant(cta.value);
        closeDialog('select_variant');
        break;
      }

      case 'open_notify_form': {
        // Surface the storefront's native notify-me UI via the active adapter
        getAdapter().triggerNotifyForm();
        closeDialog('notify');
        break;
      }

      case 'go_checkout':
        // Stash the discount code so the checkout page can read & autofill (Phase 1)
        try { sessionStorage.setItem('bbiz_pending_code', cta.value || ''); } catch (e) {}
        location.href = getAdapter().cartPath();
        break;

      case 'open_size_advisor':
        emit('size_advisor_requested');
        renderSizeAdvisorForm();
        break;

      case '_advisor_submit':
        // Internal action from the size advisor form's primary CTA
        handleSizeAdvisorSubmit();
        break;

      case 'dismiss':
      default:
        closeDialog('dismiss');
    }
  }

  // ─── R4 Size Advisor (v0.1.2) ────────────────────────────────────────
  // WARRIX runs half a size small (per brand_facts). The size system uses
  // whole steps (S, M, L, XL, 2L, 3L, ...), so "half size small" → bump up
  // by 1 step. Height < 160 / > 185 nudges the recommendation by ±1 step
  // for outliers; height is optional input.
  //
  // Result rendering picks the in-stock variant closest to the ideal size,
  // so the recommendation is always actionable (the user can buy it now).

  const SIZE_LADDER = ['S', 'M', 'L', 'XL', '2L', '3L', '5L', '7L', '9L'];

  function renderSizeAdvisorForm() {
    if (!shadowRoot) return;
    const titleEl = shadowRoot.querySelector('[data-slot="title"]');
    const msgEl = shadowRoot.querySelector('[data-slot="msg"]');
    const ctaPrimaryEl = shadowRoot.querySelector('[data-slot="cta-primary"]');
    const ctaSecondaryEl = shadowRoot.querySelector('[data-slot="cta-secondary"]');

    titleEl.textContent = 'Quick sizing help';
    msgEl.textContent = '';

    const inputStyle = 'width:100%; padding:8px 10px; font-size:14px; border:1px solid #d1d5db; border-radius:6px; font-family:inherit; background:#fff; color:#111827;';
    const labelStyle = 'display:block; margin-bottom:4px; font-size:12px; color:#6b7280; font-weight:500;';

    const row1 = document.createElement('div');
    row1.style.cssText = 'margin-bottom:12px;';
    const label1 = document.createElement('label');
    label1.style.cssText = labelStyle;
    label1.textContent = 'Your usual T-shirt size';
    const select = document.createElement('select');
    select.id = 'bbiz-adv-size';
    select.style.cssText = inputStyle;
    ['', 'S', 'M', 'L', 'XL', '2L', '3L'].forEach(function (v) {
      const opt = document.createElement('option');
      opt.value = v;
      opt.textContent = v || 'Pick one…';
      select.appendChild(opt);
    });
    row1.appendChild(label1);
    row1.appendChild(select);

    const row2 = document.createElement('div');
    row2.style.cssText = 'margin-bottom:4px;';
    const label2 = document.createElement('label');
    label2.style.cssText = labelStyle;
    label2.textContent = 'Your height in cm (optional, helps accuracy)';
    const input = document.createElement('input');
    input.type = 'number';
    input.id = 'bbiz-adv-height';
    input.placeholder = 'e.g. 170';
    input.min = '120';
    input.max = '220';
    input.style.cssText = inputStyle;
    row2.appendChild(label2);
    row2.appendChild(input);

    msgEl.appendChild(row1);
    msgEl.appendChild(row2);

    ctaPrimaryEl.textContent = 'Get my size';
    ctaSecondaryEl.textContent = 'Skip';
    ctaSecondaryEl.style.display = '';

    // Override active payload so primary CTA now triggers internal advisor submit
    activePayload = {
      ctaPrimary: { action: '_advisor_submit' },
      ctaSecondary: { action: 'dismiss' },
    };

    emit('size_advisor_started');
  }

  function handleSizeAdvisorSubmit() {
    if (!shadowRoot) return;
    const sel = shadowRoot.querySelector('#bbiz-adv-size');
    const inp = shadowRoot.querySelector('#bbiz-adv-height');
    if (!sel) return;

    const usualSize = sel.value;
    const heightVal = inp ? parseInt(inp.value, 10) : NaN;
    const heightCm = isNaN(heightVal) ? null : heightVal;

    if (!usualSize) {
      sel.style.borderColor = '#dc2626';
      return;
    }

    const idealSize = computeWarrixSize(usualSize, heightCm);
    const inStockSize = findBestInStockMatch(idealSize);

    emit('size_advisor_completed', {
      input_size: usualSize,
      input_height: heightCm,
      ideal_size: idealSize,
      recommended_size: inStockSize,
    });

    renderSizeAdvisorResult(usualSize, heightCm, idealSize, inStockSize);
  }

  function renderSizeAdvisorResult(usualSize, heightCm, idealSize, inStockSize) {
    if (!shadowRoot) return;
    const titleEl = shadowRoot.querySelector('[data-slot="title"]');
    const msgEl = shadowRoot.querySelector('[data-slot="msg"]');
    const ctaPrimaryEl = shadowRoot.querySelector('[data-slot="cta-primary"]');
    const ctaSecondaryEl = shadowRoot.querySelector('[data-slot="cta-secondary"]');

    msgEl.textContent = '';
    const heightTxt = heightCm ? ' at ' + heightCm + ' cm' : '';

    if (!inStockSize) {
      titleEl.textContent = 'Out of stock for your size';
      msgEl.textContent = 'Your ideal WARRIX size for this jersey is ' + idealSize + ", but it's not in stock right now. Take a look at our other jerseys.";
      ctaPrimaryEl.textContent = 'Browse other jerseys';
      ctaSecondaryEl.textContent = 'Close';
      activePayload = {
        ctaPrimary: { action: 'navigate', value: '/collections/jersey' },
        ctaSecondary: { action: 'dismiss' },
      };
    } else if (inStockSize === idealSize) {
      titleEl.textContent = 'Your size: ' + inStockSize;
      msgEl.textContent = 'Based on a usual ' + usualSize + heightTxt + ', we recommend size ' + inStockSize + ' for this jersey. WARRIX runs half a size small, so we sized you up.';
      ctaPrimaryEl.textContent = 'See ' + inStockSize;
      ctaSecondaryEl.textContent = 'Close';
      activePayload = {
        ctaPrimary: { action: 'select_variant', value: inStockSize },
        ctaSecondary: { action: 'dismiss' },
      };
    } else {
      titleEl.textContent = 'Closest in stock: ' + inStockSize;
      msgEl.textContent = 'Your ideal WARRIX size is ' + idealSize + ", but it's sold out. " + inStockSize + ' is the closest available size in this jersey.';
      ctaPrimaryEl.textContent = 'See ' + inStockSize;
      ctaSecondaryEl.textContent = 'Close';
      activePayload = {
        ctaPrimary: { action: 'select_variant', value: inStockSize },
        ctaSecondary: { action: 'dismiss' },
      };
    }
  }

  function computeWarrixSize(usualSize, heightCm) {
    const idx = SIZE_LADDER.indexOf(usualSize);
    if (idx === -1) return usualSize;
    let recommendedIdx = idx + 1;
    if (heightCm != null && !isNaN(heightCm)) {
      if (heightCm < 160) recommendedIdx -= 1;
      else if (heightCm > 185) recommendedIdx += 1;
    }
    recommendedIdx = Math.max(0, Math.min(recommendedIdx, SIZE_LADDER.length - 1));
    return SIZE_LADDER[recommendedIdx];
  }

  function findBestInStockMatch(idealSize) {
    if (!STATE.product || !STATE.product.variants) return null;
    const idealIdx = SIZE_LADDER.indexOf(idealSize);
    if (idealIdx === -1) return null;
    const available = STATE.product.variants.filter(function (v) {
      return !v.soldout && SIZE_LADDER.indexOf(v.value) !== -1;
    });
    if (available.length === 0) return null;
    let best = available[0].value;
    let bestDiff = Math.abs(SIZE_LADDER.indexOf(best) - idealIdx);
    for (let i = 1; i < available.length; i++) {
      const v = available[i];
      const diff = Math.abs(SIZE_LADDER.indexOf(v.value) - idealIdx);
      if (diff < bestDiff) {
        best = v.value;
        bestDiff = diff;
      }
    }
    return best;
  }


  // ─── core/trackers.js ─────────────────────────────────
  // src/core/trackers.js
  //
  // DOM event listeners that feed signals into STATE. These run in BOTH
  // arms — control needs to log behavior too, otherwise the A/B comparison
  // is meaningless.

  // ─── Active-time accounting ───────────────────────────────────────────
  // Naive `Date.now() - startTime` overcounts because it includes time
  // when the tab is hidden. We subtract those gaps by toggling on
  // visibilitychange.
  function tickActiveTime() {
    if (!document.hidden) {
      STATE.activeMs += Date.now() - STATE.activeStart;
    }
    STATE.activeStart = Date.now();
  }

  function attachActiveTimeTracker() {
    document.addEventListener('visibilitychange', tickActiveTime);
    setInterval(tickActiveTime, 1000);
  }

  // ─── Scroll depth ─────────────────────────────────────────────────────
  // IntersectionObserver would be more precise but scroll is good enough
  // and IO over the whole document is overkill. Passive listener so we
  // don't block scrolling.
  function trackScroll() {
    const docH = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    ) - window.innerHeight;
    if (docH <= 0) return;
    const pct = Math.min(100, Math.round((window.scrollY / docH) * 100));
    if (pct > STATE.scrollMaxPct) STATE.scrollMaxPct = pct;
  }

  function attachScrollTracker() {
    window.addEventListener('scroll', trackScroll, { passive: true });
  }

  // ─── Variant clicks ───────────────────────────────────────────────────
  // Capture-phase delegation so we see clicks even if the theme
  // stopPropagation()s on its own handlers. Sold-out clicks are the
  // strongest signal we have for R1.
  function attachVariantTracker() {
    document.addEventListener('click', function (e) {
      const m = getAdapter().matchVariantSwatch(e);
      if (!m) return;
      STATE.lastClickedVariant = m.value;
      STATE.lastClickedVariantSoldout = m.soldout;
      if (m.soldout) STATE.soldoutClickCount += 1;
      emit('variant_click', {
        value: m.value,
        soldout: m.soldout,
        soldout_click_count: STATE.soldoutClickCount,
      });
      if (m.soldout && typeof checkRule_R1 === 'function') checkRule_R1(m.value);
    }, true);
  }

  // ─── Add-to-cart clicks ───────────────────────────────────────────────
  // We count clicks, not successful adds. Repeated clicks usually mean
  // validation error (no size selected, etc.) and that's a signal in itself.
  function attachAddToCartTracker() {
    document.addEventListener('click', function (e) {
      if (!getAdapter().matchATCButton(e)) return;
      STATE.addToCartClicks += 1;
      emit('add_to_cart_click', { count: STATE.addToCartClicks });
    }, true);
  }

  // ─── Exit-intent (mobile + desktop) ───────────────────────────────────
  // Mobile: pagehide is the most reliable signal (covers tab-switch,
  // back-nav, app-switch). Desktop: mouseleave toward y<10 = aimed at
  // the address bar / tab bar / close button.
  function attachExitDetectors() {
    window.addEventListener('pagehide', function () {
      emit('pagehide', {
        active_ms: STATE.activeMs,
        max_scroll_pct: STATE.scrollMaxPct,
      });
      if (STATE.isMobile && typeof checkRule_R3 === 'function') checkRule_R3();
    });
    document.addEventListener('mouseleave', function (e) {
      if (e.clientY < 10 && !STATE.isMobile) {
        if (PAGE.type === 'cart' || STATE.addToCartClicks > 0) {
          // Sticky flag — R6 reads this; once true it stays true for the
          // session so a brief mouse-back-into-page doesn't unset the signal.
          STATE.exitIntentDetected = true;
          if (typeof checkRule_R3 === 'function') checkRule_R3();
          if (typeof checkRule_R6 === 'function') checkRule_R6();
        }
      }
    });
  }


  // ─── core/cart-trackers.js ─────────────────────────────────
  // src/core/cart-trackers.js
  //
  // Cart-page event tracking (Layer A1 of full-funnel instrumentation).
  // Adds visibility into the ATC → Checkout gap, which is ~41% of total
  // drop-off on warrix's 7-day funnel (46 ATC → 27 checkout starts).
  //
  // Events emitted (only on /cart pages):
  //   bbiz_cart_view              once on cart load, payload: subtotal + item count
  //   bbiz_cart_quantity_changed  on input change, payload: new qty
  //   bbiz_cart_item_removed      on remove-link click
  //   bbiz_checkout_clicked       on checkout button click, payload: subtotal
  //
  // Gating: only attached when PAGE.type === 'cart' (see boot.js).
  // All listeners use capture-phase delegation so theme handlers that
  // stopPropagation() don't hide events from us.

  function attachCartViewTracker() {
    // 500ms delay lets Elementor / theme JS finish hydrating the cart DOM
    // before we read subtotal. Theme-rendered totals occasionally lag the
    // initial document parse.
    setTimeout(function () {
      const adapter = getAdapter();
      const subtotal = adapter.getCartSubtotal ? adapter.getCartSubtotal() : null;
      const itemCount = adapter.cartItemCount ? adapter.cartItemCount() : null;
      emit('cart_view', {
        cart_subtotal: subtotal,
        cart_item_count: itemCount,
      });
    }, 500);
  }

  function attachCartQtyTracker() {
    // 'change' fires on commit (blur or stepper click). 'input' would spam
    // per-keystroke. Capture phase so theme handlers don't hide it.
    document.addEventListener('change', function (e) {
      const adapter = getAdapter();
      if (!adapter.matchCartQtyInput || !adapter.matchCartQtyInput(e)) return;
      const raw = e.target && e.target.value;
      const parsed = parseInt(raw, 10);
      emit('cart_quantity_changed', {
        new_qty: Number.isFinite(parsed) ? parsed : raw,
      });
    }, true);
  }

  function attachCartRemoveTracker() {
    document.addEventListener('click', function (e) {
      const adapter = getAdapter();
      if (!adapter.matchCartRemoveButton || !adapter.matchCartRemoveButton(e)) return;
      emit('cart_item_removed', {});
    }, true);
  }

  // Track cart drawer visibility changes. On Kalles themes (warrix.my) the
  // cart drawer is the primary cart interaction surface — users add to cart,
  // the drawer slides in, they checkout from inside the drawer. Cart-page
  // navigation (/cart) only happens for the minority who explicitly click
  // "View Cart". Without this tracker, the funnel showed "cart_view -87%
  // after ATC" which was a measurement gap, not real abandonment.
  //
  // Implementation: MutationObserver on the drawer element. We fire
  // bbiz_cart_drawer_opened once per "opening" — debounced so rapid class
  // toggles (e.g. animation classes flipping during slide-in) don't spam.
  function attachCartDrawerTracker() {
    // Defensive: wrap everything so a missing-DOM-API in any environment
    // (smoke test stubs, server-side eval, etc.) can't crash boot.
    try {
      const adapter = getAdapter();
      if (!adapter.cartDrawerElement) return;
      const el = adapter.cartDrawerElement();
      if (!el) return; // adapter says no drawer on this platform
      if (typeof MutationObserver === 'undefined') return; // env lacks observer

      let wasVisible = adapter.isCartDrawerVisible(el);
      let lastEmitAt = 0;

      function checkVisibility() {
        const nowVisible = adapter.isCartDrawerVisible(el);
        if (nowVisible && !wasVisible) {
          // Debounce: ignore re-opens within 1 second of last emit
          const now = Date.now();
          if (now - lastEmitAt > 1000) {
            lastEmitAt = now;
            const subtotal = adapter.getCartSubtotal ? adapter.getCartSubtotal() : null;
            const itemCount = adapter.cartItemCount ? adapter.cartItemCount() : null;
            emit('cart_drawer_opened', {
              cart_subtotal: subtotal,
              cart_item_count: itemCount,
            });
          }
        }
        wasVisible = nowVisible;
      }

      // Watch class-attribute mutations on the drawer (Kalles toggles `t4s-dn`).
      // attributeFilter limits observer overhead to just class changes.
      const observer = new MutationObserver(checkVisibility);
      observer.observe(el, { attributes: true, attributeFilter: ['class', 'style'] });
    } catch (e) {
      log && log('cart drawer tracker failed to attach', e);
    }
  }

  // NOTE: attachR8Interceptor was removed in v0.4.7. R8 is now a passive
  // engine-tick rule (see src/rules/R8_pre_checkout_reassurance.js) that
  // doesn't intercept any clicks. The R8 file's checkRule_R8 is wired into
  // engine.js PAGE_RULES.cart instead.

  function attachCheckoutClickTracker() {
    // Click fires BEFORE browser navigates to /checkout, so sendBeacon has
    // time to flush. (sendBeacon is keepalive-true under the hood.)
    document.addEventListener('click', function (e) {
      const adapter = getAdapter();
      if (!adapter.matchCheckoutButton || !adapter.matchCheckoutButton(e)) return;
      // Mark the user as committed so R6 doesn't fire an exit-intent dialog
      // on top of a real checkout navigation. Sticky for the session.
      STATE.checkoutClicked = true;
      const subtotal = adapter.getCartSubtotal ? adapter.getCartSubtotal() : null;
      const itemCount = adapter.cartItemCount ? adapter.cartItemCount() : null;
      emit('checkout_clicked', {
        cart_subtotal: subtotal,
        cart_item_count: itemCount,
      });
    }, true);
  }


  // ─── core/checkout-trackers.js ─────────────────────────────────
  // src/core/checkout-trackers.js
  //
  // Checkout-page event tracking (Layer A2 of full-funnel instrumentation).
  // Closes the second half of the visit→paid funnel that A1 (cart-trackers)
  // started. Specifically targets the ATC→Checkout→Paid leakage:
  //   warrix 7-day baseline: 46 ATC → 27 checkout (~41% lost) → 17 paid (~37% lost)
  //
  // Platform note:
  //   - WooCommerce (larrieshoes): checkout is on-site, widget runs on all
  //     pages, so all 3 events fire from here.
  //   - Shopify (warrix): checkout is on the *.shopify.com domain, widget
  //     does NOT run there. Equivalent events come from the Shopify Custom
  //     Pixel (shopify/bbiz-custom-pixel.js) which forwards Shopify's native
  //     analytics events to the collector with the `bbiz_shopify_*` prefix.
  //
  // Events emitted from here (WC only in practice):
  //   bbiz_checkout_started        once when /checkout page loads
  //   bbiz_payment_info_submitted  when user clicks "Place order" (final commit)
  //   bbiz_order_completed         once when /checkout/order-received/ loads
  //
  // Gating: attached only when PAGE.type matches; see boot.js.

  function attachCheckoutStartedTracker() {
    // Slight delay so cart subtotal and form fields are stable when we read them.
    setTimeout(function () {
      const adapter = getAdapter();
      const subtotal = adapter.getCartSubtotal ? adapter.getCartSubtotal() : null;
      emit('checkout_started', {
        cart_subtotal: subtotal,
      });
    }, 600);
  }

  function attachPaymentSubmitTracker() {
    // We use the *place order* click as the "commit" signal. It's the moment
    // the user has filled everything in and clicked the final button — closer
    // to a payment attempt than a payment-method radio change would be.
    // Capture phase so theme/WC handlers don't hide the event from us.
    document.addEventListener('click', function (e) {
      const adapter = getAdapter();
      if (!adapter.matchPlaceOrderButton || !adapter.matchPlaceOrderButton(e)) return;
      let paymentMethod = null;
      try {
        const checked = document.querySelector('input[name="payment_method"]:checked');
        paymentMethod = checked ? checked.value : null;
      } catch (err) { /* not WC, no radios — fine */ }
      emit('payment_info_submitted', {
        payment_method: paymentMethod,
      });
    }, true);
  }

  function attachOrderCompletedTracker() {
    // Order-received page loads naturally from the WC checkout submit. The
    // page itself confirms the order was created successfully. We emit once.
    setTimeout(function () {
      const adapter = getAdapter();
      let orderId = null;
      let orderTotal = null;
      if (adapter.extractOrderInfo) {
        const info = adapter.extractOrderInfo() || {};
        orderId = info.order_id || null;
        orderTotal = info.order_total != null ? info.order_total : null;
      }
      emit('order_completed', {
        order_id: orderId,
        order_total: orderTotal,
      });
    }, 400);
  }


  // ─── rules/R1_size_soldout_intercept.js ─────────────────────────────────
  // src/rules/R1_size_soldout_intercept.js
  //
  // R1 — Size sold-out intercept (导购式)
  //
  // WHY: 91.72% of warrix.my visitors leave without adding anything. The
  // most common pattern: they pick a popular size, it's sold out, page does
  // nothing, they bounce.
  //
  // Kalles theme already shows a "NOTIFY ME WHEN AVAILABLE" button next to
  // the ATC button when a sold-out variant is selected. So our dialog does
  // NOT push notify-me again (it would be redundant). Instead, like a real
  // sales clerk, we steer the visitor toward the rest of the SAME-TEAM jersey
  // collection — football fans care about team identity, sending an NSFC fan
  // to a Thailand jersey collection is worse than no nudge at all.
  //
  // TRIGGER: click on any [data-swatch-item].is--soldout
  // PRIMARY CTA: navigate to same-team collection ("More NSFC jerseys")
  // SECONDARY CTA: select an in-stock size (if any) or close (if fully sold out)

  // Map a product handle to a "see more from the same series" target.
  // Series map and labels are merchant-tunable via CONFIG.r1SeriesMap and
  // CONFIG.r1Fallback*. Labels are expected to already include the product-type
  // word (e.g. "NSFC jerseys", "shoes") so the message templates can compose
  // without hardcoding "jerseys" / "shoes".
  function detectSeriesForR1(handle) {
    const seriesMap = CONFIG.r1SeriesMap || {};
    const h = (handle || '').toLowerCase();
    // First series keyword that appears in the handle wins.
    for (const keyword in seriesMap) {
      if (h.indexOf(keyword.toLowerCase()) !== -1) return seriesMap[keyword];
    }
    // No keyword match — fall back to the platform's generic "browse more" URL.
    return {
      url: getAdapter().buildCollectionURL(CONFIG.r1FallbackHandle || 'jersey'),
      label: CONFIG.r1FallbackLabel || ((CONFIG.merchantName || 'our') + ' products'),
    };
  }

  function checkRule_R1(value) {
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R1') === -1) return;
    if (PAGE.type !== 'product') return;
    if (!canTrigger('R1_size_soldout_intercept')) return;

    const p = STATE.product;
    if (!p) return;

    const availableSizes = p.variants
      .filter(function (v) { return !v.soldout; })
      .map(function (v) { return v.value; });

    const series = detectSeriesForR1(p.handle);

    fireTrigger('R1_size_soldout_intercept', {
      title: availableSizes.length ? 'Size out of stock' : 'Fully sold out',
      message: availableSizes.length
        ? 'Size ' + value + ' is sold out. We still have ' + availableSizes.join(', ') +
          ' available — or take a look at more ' + series.label + '.'
        : 'This one is fully sold out. Take a look at more ' + series.label + ' in stock.',
      ctaPrimary: {
        label: 'More ' + series.label,
        action: 'navigate',
        value: series.url,
      },
      ctaSecondary: availableSizes.length
        ? { label: 'See ' + availableSizes[0], action: 'select_variant', value: availableSizes[0] }
        : { label: 'Close', action: 'dismiss' },
      series_url: series.url,
      series_label: series.label,
      clicked_variant: value,
    });
  }


  // ─── rules/R2_cart_below_free_shipping.js ─────────────────────────────────
  // src/rules/R2_cart_below_free_shipping.js
  //
  // R2 — Cart subtotal under free-shipping threshold
  //
  // WHY: 41% of users drop between cart and checkout. AOV is RM52.82 and
  // free shipping kicks in at RM120 — most carts sit in the "almost
  // there" zone. A small upsell with rationale (commonly co-bought
  // add-ons) both lifts AOV and converts checkouts.
  //
  // TRIGGER: on /cart, subtotal in (0, 120), 12s of engagement
  // PRIMARY CTA: navigate to a price-sorted accessories collection
  // SECONDARY CTA: dismiss

  function checkRule_R2() {
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R2') === -1) return;
    if (PAGE.type !== 'cart') return;
    if (!canTrigger('R2_cart_below_free_shipping')) return;
    if (STATE.activeMs < 12000) return;

    const subtotal = getCartSubtotal();
    if (subtotal == null || subtotal <= 0) return;
    if (subtotal >= CONFIG.freeShippingThreshold) return;

    const sym = CONFIG.currencySymbol || 'RM';
    const threshold = CONFIG.freeShippingThreshold;
    const addonName = CONFIG.r2AddonName || 'add-on';
    const addonPrice = CONFIG.r2AddonPrice || 0;
    const addonsStartFrom = CONFIG.r2AddonsStartingPrice || 0;
    const addonURL = CONFIG.r2AddonURL || getAdapter().buildCollectionURL('all');

    const gap = (threshold - subtotal).toFixed(2);
    const gapNum = parseFloat(gap);
    // Only promise "you'll cross the line" when the math actually works.
    // If gap > addonPrice we tell the truth instead of overpromising.
    const canUnlockWithAddon = addonPrice > 0 && gapNum <= addonPrice;

    let message;
    if (canUnlockWithAddon) {
      message = "You're " + sym + gap + " away from free shipping at " + sym + threshold +
        ". A " + addonName + " (" + sym + addonPrice + ") gets you across the line.";
    } else if (addonPrice > 0 && addonsStartFrom > 0) {
      message = "You're " + sym + gap + " away from free shipping at " + sym + threshold +
        ". Add-ons start from " + sym + addonsStartFrom + " — a " + addonName + " (" + sym + addonPrice + ") is a popular pick.";
    } else {
      // Generic copy when merchant didn't configure a specific add-on
      message = "You're " + sym + gap + " away from free shipping at " + sym + threshold +
        ". Add something small to your cart to unlock it.";
    }

    const checkoutURL = (getAdapter().checkoutPath && getAdapter().checkoutPath()) || '/checkout';

    fireTrigger('R2_cart_below_free_shipping', {
      title: 'Almost free shipping',
      message: message,
      ctaPrimary: { label: 'Browse add-ons', action: 'navigate', value: addonURL },
      // Secondary CTA was previously label="Just check out" + action="dismiss" —
      // mismatch: the label implied navigating to checkout, but action only
      // closed the dialog. Fixed to actually navigate to checkout, since users
      // who pick this option are choosing "skip the upsell and pay now".
      ctaSecondary: { label: 'Just check out', action: 'navigate', value: checkoutURL },
      cart_subtotal: subtotal,
      gap_to_free_shipping: gapNum,
    });
  }


  // ─── rules/R3_mobile_exit_intent.js ─────────────────────────────────
  // src/rules/R3_mobile_exit_intent.js
  //
  // R3 — Mobile (and desktop fallback) exit intent with non-empty cart
  //
  // WHY: 76% of warrix.my traffic is mobile. Mobile users abandon carts
  // for context-switching reasons (notification, tab swap) more than
  // desktop. A discount code with a short expiry pulls a meaningful
  // fraction back.
  //
  // TRIGGER (mobile): pagehide event AND (in cart OR has clicked ATC)
  // TRIGGER (desktop): mouseleave with clientY<10 AND (in cart OR has clicked ATC)
  //
  // PRIMARY CTA: stash the discount code and navigate to /cart
  // SECONDARY CTA: dismiss
  //
  // NOTE: the discount code itself is honored at checkout via the merchant's
  // existing Shopify discount config — we don't create discounts dynamically.

  function checkRule_R3() {
    // R3 is gated by both enabledRules (per-merchant opt-in) AND discountCode
    // being non-null (no code = no promise to surface).
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R3') === -1) return;
    if (!CONFIG.discountCode) return;
    if (!canTrigger('R3_mobile_exit_intent')) return;

    // Only fire if there's measurable purchase intent
    if (STATE.addToCartClicks === 0 && PAGE.type !== 'cart') return;

    const sym = CONFIG.currencySymbol || 'RM';

    fireTrigger('R3_mobile_exit_intent', {
      title: 'Wait — get ' + CONFIG.discountPercent + '% off your order',
      message: 'Use code ' + CONFIG.discountCode + ' at checkout for ' +
        CONFIG.discountPercent + '% off when you spend over ' + sym +
        CONFIG.discountThreshold + '.',
      ctaPrimary: {
        label: 'Take me to cart',
        action: 'go_checkout',
        value: CONFIG.discountCode,
      },
      ctaSecondary: { label: 'No thanks', action: 'dismiss' },
    });
  }


  // ─── rules/R4_jersey_size_advisor.js ─────────────────────────────────
  // src/rules/R4_jersey_size_advisor.js
  //
  // R4 — Jersey size advisor (long hesitation, no size click yet)
  //
  // WHY: Jerseys are the top sellers (4 of top 5 SKUs). Sizing is the
  // #1 source of jersey-page hesitation: WARRIX runs a half size small
  // and the size chart is buried below the fold. Users who scrolled but
  // didn't engage with the size picker are perfect for a guided sizing
  // CTA.
  //
  // TRIGGER: product page, isJersey, 25s active, scroll>40%, no variant
  //          click yet, at least one size still in stock (else this is R1
  //          territory)
  // PRIMARY CTA: open size advisor (Phase 0: just emits an event;
  //              Phase 1: opens an inline questionnaire)
  // SECONDARY CTA: dismiss

  function checkRule_R4() {
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R4') === -1) return;
    if (PAGE.type !== 'product') return;
    if (!STATE.product || !STATE.product.isJersey) return;
    if (!canTrigger('R4_jersey_size_advisor')) return;

    if (STATE.activeMs < 25000) return;
    if (STATE.scrollMaxPct < 40) return;
    if (STATE.lastClickedVariant) return;
    if (STATE.product.availableCount === 0) return;

    fireTrigger('R4_jersey_size_advisor', {
      title: 'Need help with sizing?',
      message: 'WARRIX jerseys run a half size small. Tell me your usual t-shirt size and height, and I\'ll point you to the right one — only ' +
        STATE.product.availableCount + ' sizes are still in stock for this jersey.',
      ctaPrimary: { label: 'Help me pick', action: 'open_size_advisor' },
      ctaSecondary: { label: "I'll figure it out", action: 'dismiss' },
      available_count: STATE.product.availableCount,
    });
  }


  // ─── rules/R5_facebook_low_quality.js ─────────────────────────────────
  // src/rules/R5_facebook_low_quality.js
  //
  // R5 — Facebook traffic with low engagement (likely-to-bounce rescue)
  //
  // WHY: Facebook drove 32 visits but only MYR 21 in sales last week —
  // dramatically worse conversion than direct or Google. These users
  // often arrived via an ad for a specific item and landed on a
  // generic home/collection page that doesn't match their expectation.
  //
  // TRIGGER: referrer=facebook, page is home or collection, 10s+ engaged
  //          but scroll<30% (lost, not just slow)
  // PRIMARY CTA: jump to merchant's r5Primary collection (top seller)
  // SECONDARY CTA: jump to merchant's r5Secondary collection (alt path)
  //
  // ─── COPY A/B (v0.4.6) ─────────────────────────────────────────────────
  // R5 is by far the highest-volume rule on warrix (~79% of all dialog
  // shows). Current CTR baseline is unclear — pre-v0.4.3 cta_click events
  // lacked rule field so per-rule CTR aggregated to 0. To find optimal
  // copy fast, we randomly assign each session to one of 4 variants
  // (stable per session_id hash, independent of A/B treatment/control).
  //
  // Variants are deliberately diverse in framing:
  //   A: identity-commit (which team are you here for?)
  //   B: social proof (most-bought this week)
  //   C: direct help offer (need help finding...?)
  //   D: existing baseline (passive "looking for something?")
  //
  // Each fired dialog emits variant id in event payload so we can compute
  // per-variant CTR after ~1-2 weeks of data and promote the winner.

  function pickR5Variant(sessionId) {
    // djb2-ish hash specific to R5 (different from the A/B-bucket hash so
    // variant assignment is statistically independent from treatment arm).
    let h = 5381;
    const salt = 'R5v1';
    const s = sessionId + salt;
    for (let i = 0; i < s.length; i++) {
      h = ((h << 5) + h + s.charCodeAt(i)) | 0;
    }
    return Math.abs(h) % 4;  // 0..3 → A/B/C/D
  }

  function checkRule_R5() {
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R5') === -1) return;
    if (PAGE.referrer !== 'facebook') return;
    if (!canTrigger('R5_facebook_low_quality')) return;
    if (PAGE.type !== 'home' && PAGE.type !== 'collection') return;
    if (STATE.activeMs < 10000) return;
    if (STATE.scrollMaxPct > 30) return;

    const primaryURL = CONFIG.r5PrimaryURL || getAdapter().buildCollectionURL('all');
    const primaryLabel = CONFIG.r5PrimaryLabel || 'Top picks';
    const secondaryURL = CONFIG.r5SecondaryURL || getAdapter().buildCollectionURL('all');
    const secondaryLabel = CONFIG.r5SecondaryLabel || 'Browse all';

    const variantIdx = pickR5Variant(SESSION_ID);
    const variants = [
      {
        id: 'A_identity',
        title: 'Which one are you here for?',
        message: 'Tell me your pick — I\'ll jump you straight there.',
      },
      {
        id: 'B_social_proof',
        title: 'Most-bought this week',
        message: 'Top picks from other ' + (CONFIG.merchantName || 'our') + ' customers:',
      },
      {
        id: 'C_direct_help',
        title: 'Need help finding what you came for?',
        message: '',
      },
      {
        id: 'D_baseline',
        title: 'Looking for something specific?',
        message: 'What are you in the mood for?',
      },
    ];
    const variant = variants[variantIdx];

    fireTrigger('R5_facebook_low_quality', {
      title: variant.title,
      message: variant.message,
      ctaPrimary: { label: primaryLabel, action: 'navigate', value: primaryURL },
      ctaSecondary: { label: secondaryLabel, action: 'navigate', value: secondaryURL },
      // Embed variant id in event payload. Stored in events.data jsonb.
      // Query later: SELECT data->>'r5_variant', name, COUNT(*) ...
      r5_variant: variant.id,
    });
  }


  // ─── rules/R6_cart_exit_intent.js ─────────────────────────────────
  // src/rules/R6_cart_exit_intent.js
  //
  // R6 — Cart-page exit intent
  //
  // WHY: R2 is a passive timer (12s) — it nudges users who pause on the cart.
  // R6 catches a different population: users who are actively leaving the cart
  // without checking out. warrix funnel: 46 ATC → 27 checkout starts = 41% loss.
  // R2 hits the price-gap reason; R6 hits the abandonment-decision reason
  // (distraction, doubt, "I'll come back later").
  //
  // TRIGGER PATHS (either is enough):
  //   1. Desktop exit-intent: mouseleave with cursor pointing at the top of
  //      the viewport (address bar / tab bar / close button). Fired from
  //      trackers.js's mouseleave handler.
  //   2. Stalled cart: 25s+ active time on /cart, low scroll, no checkout
  //      click. Mobile-friendly fallback since mobile has no mouseleave.
  //      Fired from engine.js tick.
  //
  // REQUIRED STATE:
  //   - PAGE.type === 'cart'
  //   - cart subtotal > 0 (cart has items)
  //   - STATE.checkoutClicked === false (user hasn't committed yet)
  //   - canTrigger() passes (cooldown + per-session-once)
  //
  // CTA: direct navigate to /checkout (via adapter.checkoutPath()) rather
  // than another nudge step. The point is to keep the path frictionless once
  // the user signals "I'm leaving — convince me to convert."
  //
  // Note: this rule does NOT offer a discount (R3's job). Discount-free
  // reassurance is intentional — preserves margin and avoids training users
  // to wait for popups.

  function checkRule_R6() {
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R6') === -1) return;
    if (PAGE.type !== 'cart') return;
    if (!canTrigger('R6_cart_exit_intent')) return;
    if (STATE.checkoutClicked) return;

    const subtotal = getCartSubtotal();
    if (subtotal == null || subtotal <= 0) return;

    // One of two signals must be true.
    const stalled = STATE.activeMs > 25000 && STATE.scrollMaxPct < 20;
    const exitIntent = !!STATE.exitIntentDetected;
    if (!stalled && !exitIntent) return;

    const adapter = getAdapter();
    const checkoutURL = (adapter.checkoutPath && adapter.checkoutPath()) || '/checkout';
    const sym = CONFIG.currencySymbol || 'RM';

    // Copy is intentionally low-pressure and factually accurate (post-R7 review):
    //   - No "we're holding" (we don't hold anything — cart is cookie-persistent)
    //   - No "can sell out fast" (generic FOMO, no information value)
    //   - Secondary CTA "Close" instead of "Keep browsing" (don't verbally
    //     suggest they leave; the X click already lets them dismiss).
    fireTrigger('R6_cart_exit_intent', {
      title: "Your cart is saved",
      message: "Your " + sym + subtotal.toFixed(2) + " of items are still in your cart. " +
        "Come back any time — or finish now with one tap.",
      ctaPrimary: { label: 'Complete checkout', action: 'navigate', value: checkoutURL },
      ctaSecondary: { label: 'Close', action: 'dismiss' },
      cart_subtotal: subtotal,
      trigger_signal: exitIntent ? 'exit_intent' : 'stalled',
      active_ms: STATE.activeMs,
      scroll_max_pct: STATE.scrollMaxPct,
    });
  }


  // ─── rules/R7_cart_urgency.js ─────────────────────────────────
  // src/rules/R7_cart_urgency.js
  //
  // R7 — Cart-page urgency banner
  //
  // WHY: Some users on the cart page are "I'll come back later" lingerers
  // — they've decided to buy but are deferring. A soft urgency cue ("your
  // items are held for 15 minutes") pushes a fraction of them to complete now
  // instead of returning to a dead session.
  //
  // RISK ACKNOWLEDGEMENT: This is a soft urgency cue, not a real stock reserve.
  // Users may notice if they reload an hour later and the cart's still there.
  // We accept this risk because (a) most Shopify/WC carts do persist for
  // sessions/cookies anyway, and (b) the message is phrased as "we're holding
  // X for the next 15 min" — not "or it will be gone." But we still run this
  // in an A/B test so we can measure trust-cost via downstream metrics:
  // repeat-visit rate, refund rate, support tickets. If those degrade, kill R7.
  //
  // TRIGGER (all required):
  //   - PAGE.type === 'cart'
  //   - 30s+ active time (lingerer signal, distinguishes from quick checkers)
  //   - cart subtotal > 0 (cart has items)
  //   - cart_item_count <= 2 (urgency makes less sense on big multi-item carts)
  //   - STATE.checkoutClicked === false (user hasn't committed already)
  //   - canTrigger() passes (cooldown + per-session-once)
  //
  // Why item count gate: R7 mainly targets the "single item, deciding" scenario.
  // A user with 5 items already mentally committed — urgency feels manipulative.

  function checkRule_R7() {
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R7') === -1) return;
    if (PAGE.type !== 'cart') return;
    if (!canTrigger('R7_cart_urgency')) return;
    if (STATE.checkoutClicked) return;
    if (STATE.activeMs < 30000) return;

    const adapter = getAdapter();
    const subtotal = adapter.getCartSubtotal ? adapter.getCartSubtotal() : null;
    if (subtotal == null || subtotal <= 0) return;

    const itemCount = adapter.cartItemCount ? adapter.cartItemCount() : null;
    if (itemCount != null && itemCount > 2) return;

    const checkoutURL = (adapter.checkoutPath && adapter.checkoutPath()) || '/checkout';
    const sym = CONFIG.currencySymbol || 'RM';

    fireTrigger('R7_cart_urgency', {
      title: 'Your cart is being held ⏳',
      message: "We're holding " + sym + subtotal.toFixed(2) + " of items for the next 15 minutes. " +
        "Complete checkout now to lock in current sizes and prices — popular items can sell out fast.",
      ctaPrimary: { label: 'Complete checkout now', action: 'navigate', value: checkoutURL },
      ctaSecondary: { label: "I'll come back later", action: 'dismiss' },
      cart_subtotal: subtotal,
      cart_item_count: itemCount,
      active_ms: STATE.activeMs,
    });
  }


  // ─── rules/R8_pre_checkout_reassurance.js ─────────────────────────────────
  // src/rules/R8_pre_checkout_reassurance.js
  //
  // R8 — Cart-page checkout reassurance (REDESIGNED v0.4.7)
  //
  // HISTORY: v0.3.x R8 was a click-intercept modal that fired the moment a user
  // clicked the cart's Checkout button. Live review on warrix (2026-05-18)
  // showed it was a reverse funnel — alarming "safety check" framing + a
  // "Wait, let me re-check my cart" secondary CTA that gave hesitating users
  // a polite exit. Disabled the same day in v0.4.2.
  //
  // REDESIGN PRINCIPLES (v0.4.7):
  //   - NO interruption of any checkout-click flow. R8 never sits between
  //     user and the next step.
  //   - Fires only when R2 (free-shipping nudge) doesn't apply — i.e. the
  //     user already unlocked free shipping. So R8 is the answer to "what
  //     do we say to a user whose cart is past the magic number and just
  //     standing on /cart?"
  //   - Factual reassurance (delivery, payment, returns), no urgency.
  //   - Secondary CTA is a no-op close (no "Close" / "Not now" text that
  //     might prompt deferral).
  //
  // TRIGGER (all required):
  //   - PAGE.type === 'cart'
  //   - 8s+ active time (light dwell — they're considering)
  //   - cart subtotal >= CONFIG.freeShippingThreshold
  //     (so R2's "almost free shipping" math doesn't apply to this user)
  //   - STATE.checkoutClicked === false (haven't committed yet)
  //   - canTrigger() passes (cooldown + per-session-once)

  function checkRule_R8() {
    if (!CONFIG.enabledRules || CONFIG.enabledRules.indexOf('R8') === -1) return;
    if (PAGE.type !== 'cart') return;
    if (!canTrigger('R8_pre_checkout_reassurance')) return;
    if (STATE.checkoutClicked) return;
    if (STATE.activeMs < 8000) return;

    const adapter = getAdapter();
    const subtotal = adapter.getCartSubtotal ? adapter.getCartSubtotal() : null;
    if (subtotal == null || subtotal <= 0) return;
    // Only fire AFTER free-shipping threshold is met — leaves the
    // sub-threshold cart-state to R2 alone.
    if (subtotal < CONFIG.freeShippingThreshold) return;

    const checkoutURL = (adapter.checkoutPath && adapter.checkoutPath()) || '/checkout';
    // Reassurance copy. Merchant can override via CONFIG.r8Reassurance if they
    // want to call out specific payment methods (FPX, Apple Pay, etc.) or
    // returns terms. Default is generic but accurate for both pilot stores.
    const reassurance = CONFIG.r8Reassurance || (
      '✓ Local delivery · Secure checkout · Returns within 7 days on unworn items'
    );

    fireTrigger('R8_pre_checkout_reassurance', {
      title: 'Ready to check out?',
      message: reassurance,
      ctaPrimary: { label: 'Continue to checkout', action: 'navigate', value: checkoutURL },
      // Plain "Close" — no defer-inducing language. The X button at top-right
      // already provides a dismissal path; the secondary CTA is symmetric to
      // primary so the layout doesn't shift.
      ctaSecondary: { label: 'Close', action: 'dismiss' },
      cart_subtotal: subtotal,
    });
  }


  // ─── core/spa.js ─────────────────────────────────
  // src/core/spa.js
  //
  // SPA route-change handling. Originally written for the small number of
  // Shopify themes that use client-side navigation; now also required for
  // SiteGiant Webstore (Next.js App Router, every route change is SPA).
  //
  // What "route change" means here: the URL pathname changes WITHOUT a full
  // document reload. We catch this via:
  //   - popstate            (back/forward button)
  //   - history.pushState   (in-app navigation)
  //   - history.replaceState (some SPAs use this for forward routes too)
  //
  // On route change we:
  //   1. Re-detect PAGE.type via the active adapter
  //   2. Reset page-scoped engagement counters (activeMs, scroll, etc.)
  //   3. Re-extract product data when entering a PDP
  //   4. Re-fire one-shot page-load events (cart_view, checkout_started,
  //      order_completed) because they're setTimeout calls inside attach*
  //      functions, not document-level listeners
  //
  // What we DO NOT reset:
  //   - STATE.triggered (rule-firing record) — caps still apply per session
  //   - STATE.checkoutClicked / exitIntentDetected — session-sticky
  //   - STATE.addToCartClicks (in current code this IS reset; keep it that
  //     way for backwards-compat with warrix where SPA nav is rare)

  function onSpaRouteChange() {
    const newType = getAdapter().detectPageType();
    if (newType === PAGE.type) return;
    emit('route_change', { from: PAGE.type, to: newType });
    PAGE.type = newType;
    STATE.activeMs = 0;
    STATE.scrollMaxPct = 0;
    STATE.addToCartClicks = 0;
    STATE.soldoutClickCount = 0;
    STATE.lastClickedVariant = null;
    STATE.pageEnteredAt = Date.now();

    // Re-extract product when entering a PDP. Other page types don't have a
    // product context (cart's product set is rendered in DOM rows, handled
    // per-row by rules that need it).
    if (newType === 'product') {
      try { STATE.product = getAdapter().extractProduct(); }
      catch (e) { log('extractProduct error on route change', e); }
    }

    // Re-fire one-shot trackers gated on page type. Each function is defined
    // in cart-trackers.js / checkout-trackers.js — they're hoisted into the
    // shared IIFE closure by the build's concat step, so we can call them
    // directly here without any window.* exposure.
    if (newType === 'cart' && typeof attachCartViewTracker === 'function') {
      attachCartViewTracker();
    }
    if (newType === 'checkout' && typeof attachCheckoutStartedTracker === 'function') {
      attachCheckoutStartedTracker();
    }
    if (newType === 'order_complete' && typeof attachOrderCompletedTracker === 'function') {
      attachOrderCompletedTracker();
    }
  }

  function attachSpaListeners() {
    window.addEventListener('popstate', onSpaRouteChange);

    // Patch pushState. Browsers don't fire any event on pushState by design.
    const _push = history.pushState;
    history.pushState = function () {
      _push.apply(this, arguments);
      onSpaRouteChange();
    };

    // Patch replaceState too — Next.js App Router uses replaceState for some
    // route transitions (notably the initial /post-load redirect-to-canonical
    // and some client-side filter changes).
    const _replace = history.replaceState;
    history.replaceState = function () {
      _replace.apply(this, arguments);
      onSpaRouteChange();
    };
  }


  // ─── core/engine.js ─────────────────────────────────
  // src/core/engine.js
  //
  // Master tick loop. Runs every second, drives the state machine,
  // invokes per-page rules.
  //
  // To register a new rule:
  //   1. Create src/rules/RX_my_rule.js with checkRule_RX(...)
  //   2. Add an entry to PAGE_RULES below, scoped to the right page type
  //
  // Rules that fire on a DOM event (R1, R3) are called from src/core/trackers.js,
  // not from this loop.

  const PAGE_RULES = {
    // R4 re-enabled in 0.1.2 — its inline 2-question size advisor is implemented
    // (see renderSizeAdvisorForm in dialog.js).
    product:    ['checkRule_R4'],
    // R2 = passive timer-based free-shipping nudge.
    // R6 = exit-intent / stalled-cart intercept; complements R2 by hitting the
    //      abandonment-decision moment rather than the price-gap moment.
    // R2: passive 12s free-shipping-gap nudge (cart < threshold).
    // R6: exit-intent / stalled-cart intercept.
    // R7: deprecated as of v0.4.2 — kept in build for future redesign;
    //     not in PAGE_RULES so it's never invoked from the tick.
    // R8: 8s passive reassurance for cart >= threshold (v0.4.7 redesign,
    //     no longer a click interceptor — purely engine-tick driven).
    cart:       ['checkRule_R2', 'checkRule_R6', 'checkRule_R8'],
    collection: ['checkRule_R5'],
    home:       ['checkRule_R5'],
  };

  function tick() {
    evaluateStateMachine();

    const rules = PAGE_RULES[PAGE.type] || [];
    for (let i = 0; i < rules.length; i++) {
      const fn = window[rules[i]];
      if (typeof fn === 'function') {
        try {
          fn();
        } catch (e) {
          log('rule error', rules[i], e);
        }
      }
    }
  }

  function startEngine() {
    setInterval(tick, 1000);
  }


  // ─── core/boot.js ─────────────────────────────────
  // src/core/boot.js
  //
  // Entry point. Wires everything up after DOMContentLoaded.
  //
  // This file exists separately from the IIFE wrapper added by the build
  // step so each module above can declare top-level `const` and have them
  // hoist into the same closure scope after concatenation.

  function boot() {
    emit('session_start', {
      ab_bucket: AB_BUCKET,
      page_type: PAGE.type,
      referrer: PAGE.referrer,
      mobile: STATE.isMobile,
    });

    // Populate STATE.product on initial product-page load.
    //
    // Before v0.4.4 this was missing — spa.js sets STATE.product on SPA route
    // changes, but full-reload landings (the default for warrix's Shopify
    // Kalles theme) never went through that path, so STATE.product stayed null.
    // That silently killed R1 (sold-out intercept) for the entire pilot:
    //   checkRule_R1 has `if (!STATE.product) return` which short-circuited
    //   every click on a sold-out variant. 7 days × 1251 sessions = 0 R1
    //   triggers in production. Same bug affected R4 (jersey size advisor)
    //   but it's been disabled by default so nobody noticed.
    if (PAGE.type === 'product') {
      try { STATE.product = getAdapter().extractProduct(); }
      catch (e) { log('extractProduct error on boot', e); }
    }

    attachActiveTimeTracker();
    attachScrollTracker();
    attachVariantTracker();
    attachAddToCartTracker();
    attachExitDetectors();
    attachSpaListeners();
    // Cart drawer is platform-specific (Kalles slide-in, etc.). Attached
    // globally because the drawer can open on any page after ATC, not just
    // when PAGE.type === 'cart'. Adapter no-ops on platforms without drawers.
    attachCartDrawerTracker();

    // Cart-page instrumentation (Layer A1 — full-funnel visibility).
    // Gated on page type so we don't waste listeners on non-cart pages.
    //
    // v0.4.7: removed attachR8Interceptor — R8 was redesigned as a passive
    // engine-tick rule (no click intercept). See engine.js PAGE_RULES.cart.
    if (PAGE.type === 'cart') {
      attachCartViewTracker();
      attachCartQtyTracker();
      attachCartRemoveTracker();
      attachCheckoutClickTracker();
    }

    // Checkout / order-complete instrumentation (Layer A2).
    // Shopify (warrix) doesn't reach these pages with the widget — its
    // equivalent events come from the Shopify Custom Pixel. WooCommerce
    // (larrieshoes) runs the widget on every page, so we fire from here.
    if (PAGE.type === 'checkout') {
      attachCheckoutStartedTracker();
      attachPaymentSubmitTracker();
    }
    if (PAGE.type === 'order_complete') {
      attachOrderCompletedTracker();
    }

    startEngine();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Debug hook — accessible as window.__BBIZ__ in DevTools
  window.__BBIZ__ = {
    state: STATE,
    config: CONFIG,
    page: PAGE,
    sessionId: SESSION_ID,
    abBucket: AB_BUCKET,
    forceShow: showDialog,
    close: closeDialog,
  };

  // Expose rules to window so the engine can dispatch them by name.
  // (After IIFE wrap, all top-level functions are in closure scope —
  //  this re-exports the ones that the dispatcher in engine.js needs.)
  window.checkRule_R1 = checkRule_R1;
  window.checkRule_R2 = checkRule_R2;
  window.checkRule_R3 = checkRule_R3;
  window.checkRule_R4 = checkRule_R4;
  window.checkRule_R5 = checkRule_R5;
  window.checkRule_R6 = checkRule_R6;
  window.checkRule_R7 = checkRule_R7;
  window.checkRule_R8 = checkRule_R8;

})();
